// Shared third-party mark library — banks, brokerages, exchanges, real
// estate, valuation services, and any other external platform referenced
// in the spreadsheet's Location columns. Used by BudgetPage (funding source
// indicators) and AssetsPage (where each asset / holding is held).
//
// Brands available in simple-icons via react-icons/si are imported directly.
// Anything else is rendered from inline SVGs that the user provides; the
// `PlaceholderBadge` below is the fallback if an SVG hasn't been supplied
// yet for a given platform.

import { useId } from 'react';
import { SiApple, SiChase, SiCloudflare, SiDropbox, SiWellsfargo } from 'react-icons/si';
import { Tooltip } from './Tooltip';

type LogoProps = { size?: number };

// --- Official brand marks from simple-icons ---

export function RobinhoodLogo({ size = 18 }: LogoProps) {
  // Official Robinhood feather mark in #CCFF00 (yellow-green).
  return (
    <svg
      role="img"
      aria-label="Robinhood"
      viewBox="0 0 150 150"
      width={size}
      height={size}
    >
      <g fill="#CCFF00">
        <path d="M91.1,33.8h-30.5c-1.1,0-2,.4-2.8,1.4l-21.9,27.1c-3.2,4-4,7.7-4,13v27.7c-7.1,20-11.6,33.5-14.9,45.7-.2.8.1,1.2.8,1.2h3.3c.6,0,1.2-.3,1.4-.8,25-63.6,52.2-95.1,69.2-114,.7-.8.4-1.4-.6-1.4Z" />
        <path d="M92,2.6c-2,.8-4,2.1-4.9,2.9-9,7.7-15,13.8-20.8,19.9-.7.7-.4,1.4.6,1.4h33.8c3.1,0,4.9,1.8,4.9,4.9v38.1c0,1,.8,1.3,1.4.4l20.4-26.6c3.3-4.3,4.3-5.6,5.2-11.6,1.2-8.8.5-22.4-4.8-28-4.7-5-26-5.2-35.8-1.4Z" />
        <path d="M97.1,41.5c-21,23.4-37.3,48-52.5,77.5-.4.7.1,1.4,1,1.1l31.3-9.6c3.5-1.1,5.5-2.5,7.2-5.3l13.9-23c.3-.6.4-1.3.4-1.8v-38.3c0-1-.7-1.4-1.4-.6Z" />
      </g>
    </svg>
  );
}

export function CoinbaseLogo({ size = 18 }: LogoProps) {
  // Official Coinbase mark — flat-sided ring forming a "C" in #0052FF.
  return (
    <svg role="img" aria-label="Coinbase" viewBox="0 0 512 512" width={size} height={size}>
      <path
        fill="#0052FF"
        d="M256.6,384.8c-70.9,0-128.2-57.4-128.2-128.2s57.4-128.2,128.2-128.2,116.1,46.2,126.3,106.8h129.1C501.1,103.6,391,.1,256.5.1S0,115,0,256.6s114.9,256.5,256.5,256.5,244.6-103.5,255.5-235.1h-129.3c-10.1,60.6-62.7,106.8-126.1,106.8Z"
      />
    </svg>
  );
}

export function CashAppLogo({ size = 18 }: LogoProps) {
  // Official Cash App "$" mark in #00D54B.
  return (
    <svg
      role="img"
      aria-label="Cash App"
      viewBox="0 0 42.7 42.9"
      width={size}
      height={size}
    >
      <path
        fill="#00D54B"
        fillRule="evenodd"
        d="M36.4,9.8c0,.3-.1.7-.4.9l-2.5,2.5c-.4.6-1.3.6-1.8.1-2.4-2.1-5.5-3.1-8.6-3.1s-5.2.9-5.2,3.3,2.4,3.1,5.2,4.2c.3.1.6.1.9.3,5.7,1.9,10.4,4.3,10.4,9.8s-4.8,10.1-12.4,10.7l-.7,3.3c-.1.6-.6,1-1.3,1h-4.8c-.4,0-.7-.1-1-.4-.3-.3-.3-.7-.3-1l.7-3.4c-2.8-.7-5.5-2.2-7.7-4.3-.3-.3-.4-.6-.4-.9s.1-.7.4-.9l2.7-2.7c.4-.4,1.3-.4,1.8,0,2.4,2.4,5.8,3.6,9.1,3.6s5.8-1.5,5.8-3.9-1.9-2.8-5.7-4.2c-.4-.1-.7-.3-1.2-.4-4.8-1.6-9.2-4.2-9.2-9.7s5.4-9.7,11.9-10l.7-3.3c0-1,.6-1.3,1.2-1.3h4.8c.4,0,.7.1,1,.4.3.3.3.7.3,1l-.7,3.7c2.4.7,4.8,2.1,6.7,3.7.3.3.4.6.4.9Z"
      />
    </svg>
  );
}

export function ChaseLogo({ size = 18 }: LogoProps) {
  return <SiChase color="#117ACA" size={size} />;
}

export function WellsFargoLogo({ size = 18 }: LogoProps) {
  return <SiWellsfargo color="#D71E28" size={size} />;
}

export function MidwestLoanServicesLogo({ size = 18 }: LogoProps) {
  // Official Midwest Loan Services mark — interlocking blue mountain/M
  // shape in dark navy (#0B5484) and brand blue (#1682C6).
  return (
    <svg
      role="img"
      aria-label="Midwest Loan Services"
      viewBox="0 0 141.9 141.9"
      width={size}
      height={size}
    >
      <path
        fill="#0B5484"
        d="M103.9,8.1l-3.7,6.7C91.4,10.2,81.5,7.6,71,7.6C36,7.6,7.6,36,7.6,71c0,6.6,1,12.9,2.9,18.9L16.8,76V45.4h10.8v6.9l7.8-17.2h19.1L46,53.4L20,108.6c4.4,6,9.9,11.1,16.1,15.2l-4.2,6.4c-5.8-3.8-11.1-8.5-15.5-13.9c-4.2-5.1-7.8-10.7-10.4-16.9C2.1,90.8,0,81.1,0,71C0,31.8,31.8,0,71,0C82.8,0,94,2.9,103.9,8.1z"
      />
      <polygon fill="#1682C6" points="81,79.4 71,96.8 46,53.4 54.5,35.1 71,62.6" />
      <path
        fill="#1682C6"
        d="M141.9,71c0,10.3-2.2,20-6.1,28.8c-2.7,6-6.1,11.5-10.3,16.5c-13,15.6-32.6,25.6-54.6,25.6c-9.5,0-18.7-1.9-27-5.3l4-6.6c7.1,2.8,14.9,4.3,23,4.3c20.9,0,39.4-10.1,51-25.7L96,53.4l10.5-18.3l24.8,55.1c1.9-6.1,3-12.5,3-19.2c0-19.8-9.1-37.5-23.3-49.1l3.9-6.6C131.4,28.2,141.9,48.4,141.9,71z"
      />
      <polygon fill="#0B5484" points="106.5,35.1 96,53.4 81,79.4 71,62.6 87.4,35.1" />
    </svg>
  );
}

export function AmazonPrimeLogo({ size = 18 }: LogoProps) {
  // Official Amazon Prime wordmark in Prime blue (#0779FF). Wide aspect
  // (~1.7:1) — at icon-column sizes the wordmark reads small but stays
  // recognizable; tooltip carries the full name on hover.
  return (
    <svg
      role="img"
      aria-label="Amazon Prime"
      viewBox="0 0 4605.7 2723.6"
      width={size}
      height={size}
    >
      <path
        fill="#0779FF"
        d="M2246 2723.1a2591.3 2591.3 0 0 1-591.4-83.5 2159.7 2159.7 0 0 1-516.8-208.3A1728.5 1728.5 0 0 1 679.1 2060c-55.6-63.6-86-106.3-101-141.4a93 93 0 0 1-9-35.5c-1.2-22.7 6-36.6 23.6-45.2 6.3-3.2 7-3.3 18.6-3.3a51 51 0 0 1 19 2.3c20.3 6.6 46.1 22.7 79.5 49.4a2069.6 2069.6 0 0 0 372.5 240.3c147.7 74.5 292.3 128.2 473.7 176a3157 3157 0 0 0 627.8 98.4c72.8 4.1 93.8 4.6 189.5 4.6 105.9 0 154.2-1.5 242-7.5 361.5-24.8 718.7-108.9 984.8-231.8a2156 2156 0 0 0 63-31c45.1-22.7 57-28.2 75.7-35 26.3-9.4 44.4-6.3 59.7 10.2a50.4 50.4 0 0 1 14.8 38.5c-5.1 41.5-59 100.4-163.7 179.3-249.6 188-594.4 319.3-974.8 371.2a2718 2718 0 0 1-263.5 22.6 3806 3806 0 0 1-165.4 1zm1615-239.4a51.1 51.1 0 0 1-26.8-24c-7.7-16.8-5.3-31.5 10.4-64.1a572 572 0 0 1 31.9-56.5c41.2-68.3 82.6-159.4 102.1-224.6 19.6-65.4 22-102.6 8-123.8-12-18.2-50.5-29.6-113.2-33.7-24.5-1.6-103.2-.6-126.7 1.5-52.5 4.9-89 10.2-138 20.1-35 7.1-41.8 8-66.5 8-23.9 0-28.8-.7-38.5-5.5a41 41 0 0 1-21-41.6c2.5-13.7 11.4-26.6 27.8-40.3 41.9-35 107.4-69 178.7-92.6a654.9 654.9 0 0 1 232.5-33.3 596 596 0 0 1 133 19.4c71.2 18.4 114.3 42 129.3 71 11.5 22.3 15.8 44.9 15.7 83.6a617 617 0 0 1-33.6 195.2 608 608 0 0 1-156.5 249.2c-53 52.3-89.7 80-119.5 90a57.3 57.3 0 0 1-29 2zm-3838.6-729a39.2 39.2 0 0 1-19.9-17.9l-2.3-4.2-.2-656.5c-.2-594.8-.1-657 1.3-661.9 2-6.9 10.3-16 18.3-19.8l5.6-2.8h181l4.7 2.2a34.4 34.4 0 0 1 16.7 17.1c3.6 7.7 4.7 12.9 19.3 84.5l10.3 50.7h10l4.5-9.7a318.5 318.5 0 0 1 38.7-63.3 322.1 322.1 0 0 1 65.6-59.3 278.7 278.7 0 0 1 218.2-40.6c76.2 16.7 145 67.4 195 143.7 48.3 73.8 82.2 179.6 93 290.7 3.2 33 3.7 42.2 4.2 89.7a905 905 0 0 1-6.7 140.3c-16 127.7-59.3 238.2-122 311a470.3 470.3 0 0 1-46 44.7 285.6 285.6 0 0 1-137 60c-19.6 3-66.8 3.3-85.5.5-72.7-10.9-127.5-44.7-175.8-108.5a491.5 491.5 0 0 1-36-56.4c-2.7-4.7-2.8-4.8-7.7-4.8h-5l-.2 224.3-.3 224.2-3 5.3a40.6 40.6 0 0 1-18.1 16.6c-4.2 1.4-16 1.6-110.8 1.6-86.6 0-106.8-.3-109.9-1.4zM466 1231c38-7.3 68.2-28.8 92-65.5 30-46 47.6-113 52.9-201.8 1.6-26.5.7-105.1-1.5-127.7-4.6-48-12.1-87-23-118.7-25.2-73.5-64.5-114.7-120.4-126a222 222 0 0 0-57.3 0c-45.8 9.2-80.6 38.3-106.3 88.7a346 346 0 0 0-31 94c-12.9 65.2-15.2 163.8-5.5 239.4 8.2 64.1 25.4 116.1 50.7 153.6 27 39.7 60.5 60.9 104.7 66 10.8 1.3 32.9.3 44.7-2zm3677.3 224.4-15.5-1.5a408 408 0 0 1-207.3-78.7 477 477 0 0 1-78.6-75.9c-64-80.8-103.7-183.6-115.5-299.8-3.1-30.9-3.8-50-3.3-92.7a661 661 0 0 1 10.3-120.8c37.3-212.7 172.2-371.6 348-409.8 32.2-7 57.7-9.6 94.4-9.6a382 382 0 0 1 144.5 26.1 505.2 505.2 0 0 1 51.3 24.9 426.6 426.6 0 0 1 135 127.6 588 588 0 0 1 85.6 210.8c10.3 51.4 14 96.8 13.5 167-.3 42.6-.4 43.6-2.6 47.6a31.6 31.6 0 0 1-22.1 17c-4.6 1.1-57.6 1.4-293.5 1.4h-287.8l.6 6.8c8 86.2 23.8 142.4 51.7 184.1 23.3 35 51.9 55 90 63.2 12.2 2.6 41.2 3.6 55.8 2 39.6-4.6 68.5-18 92.6-43.3 19.4-20.3 32.7-45.7 42.3-80.8 4.9-18 6-20.7 10.8-26 5.2-5.8 11-8.3 19.3-8.3 9 0 197.8 42.2 205.6 45.9a24 24 0 0 1 14 25.3c-2 15.6-24.8 68.7-42.6 99.7a387 387 0 0 1-161.5 154.2 437.6 437.6 0 0 1-148.5 41.8c-13.6 1.5-76 2.8-86.5 1.8zm192.1-648.6c-1.3-14-5.7-42.5-9.1-59.7-16.1-79.3-48.4-134.1-92.4-157a133.2 133.2 0 0 0-117.6-.3 133 133 0 0 0-37.5 28.2 173.4 173.4 0 0 0-32.7 43.2c-19 34.3-33.3 80.5-41.2 133.8-1.1 7.4-2 14.7-2 16.3v2.7H4336l-.7-7.2zm-3283 621.4a39 39 0 0 1-18-18.8c-1.4-4.3-1.6-47.8-1.6-499.4 0-447.5.2-495 1.6-499.3a29.4 29.4 0 0 1 15.6-16.3l6.3-2.9 87.5-.3c91-.3 95.3-.2 103.1 3.8 5.5 2.8 12.4 11 14.8 17.7 1.2 3.2 7.8 33.5 14.6 67.3l13 63.8c.6 2 1 2.3 5.6 2l5-.3 5.3-12a330.4 330.4 0 0 1 42.2-71.5c9.5-11.9 30-32 42-41.1 46.7-35.6 96.4-52 158.3-51.9 42 0 79 7.2 113 21.8 11.2 4.8 17.3 9.7 21 16.5 2.4 4.8 2.6 6.1 2.6 16.2 0 11-.1 11.3-24.8 111-13.6 55-25.5 102-26.3 104.2-2 5.2-7.2 10.8-12.2 13a31 31 0 0 1-11.2 1.6c-8.5 0-9.2-.2-43.7-14.2-36.1-14.7-63.3-23.2-84.3-26.2a267.2 267.2 0 0 0-55 .4 142 142 0 0 0-51.7 18.9c-12 7.4-30.7 26-39.5 39.3-24.3 36.9-35.3 79.5-37.8 145.5-.5 13.5-.9 151.7-1 307 0 312.7.6 286.5-6.4 296.1a23.8 23.8 0 0 1-9.2 7.5l-5.9 2.9-108.5.3-108.5.2zm745 1.4c-9.8-3-16.1-9-19.3-18-1.7-4.9-1.8-27.7-1.8-501.1v-496l2.6-5.5c3.3-7 8-11.5 15-14.9l5.4-2.6 105.5-.3c109.3-.3 114.3-.1 122 3.8a30 30 0 0 1 11.8 12.8c1.7 3.5 1.8 30 2 500.7.2 453.3.1 497.4-1.3 502.5a23 23 0 0 1-7.3 11.1c-9.5 9.5-.6 9-123.6 8.8-83.4 0-108-.3-111-1.3zm449.3 0c-8.3-3-15.6-9.5-20-17.8l-2.4-4.3v-994l2.4-5.3a28.2 28.2 0 0 1 15.1-14.1l5.5-2.6h91c84.7 0 91.4.1 95.6 1.8a32 32 0 0 1 19 19.5c1.7 4.6 10.5 46.2 27 128l1 5.2h10.5l3.5-8.8a413.7 413.7 0 0 1 25.1-49.7c40.4-64.6 98.8-104.9 169.8-117 47.2-8.1 99.7-4.2 142.8 10.6 56.5 19.4 103.7 58 138.5 113.4a431.7 431.7 0 0 1 23.4 44.8l2.9 6.7h10l8-12.2c35.9-55 80.4-100.5 124.5-127.2a295 295 0 0 1 194.4-37 258.8 258.8 0 0 1 171.2 103c12 16 21.3 31.3 31.4 51.9 25.9 52.6 42 112.8 48.5 181.7 3.4 35.7 3.7 63.6 3.3 384.3l-.3 317-2.6 5.5a27.7 27.7 0 0 1-14.1 15.1l-5.3 2.4h-219l-4.3-2.3a41.3 41.3 0 0 1-15.7-15.4l-3-5.3-.5-320c-.6-340.2-.4-324.1-5-351-9.7-57.3-35.1-97.2-73.5-115.6-19-9.1-37.6-12.5-63.8-11.6-37 1.3-64.6 12.7-88.1 36.2-28.2 28.1-44.5 69.5-52.3 132.5-1.5 12.3-1.7 42-2.2 321.5l-.6 308-2.6 5.5a27.7 27.7 0 0 1-14.1 15.1l-5.3 2.4h-219l-5.2-3a40.5 40.5 0 0 1-15.4-15.6l-2.4-4.4-.5-320c-.6-340.2-.4-324.1-5-351-9.7-57.3-35.1-97.2-73.5-115.6-19-9.1-37.6-12.5-63.8-11.6-37 1.3-64.6 12.7-88.1 36.2-28.2 28.1-44.5 69.5-52.3 132.5-1.5 12.3-1.7 42-2.2 321.5l-.6 308-2.6 5.5a27.7 27.7 0 0 1-14.1 15.1l-5.3 2.4-108 .2c-86.6.2-108.7 0-111.6-1zM1887.8 309a154 154 0 0 1-94-46.7 139.7 139.7 0 0 1-37-72c-3-15-3.8-44-1.6-60.8 6-45 26.5-79 62.3-103C1869-8 1944.5-9 1995.4 24.2a154 154 0 0 1 48.3 49.5c21.5 36.4 26 93 11.1 137.5a147.5 147.5 0 0 1-85.6 88c-23.8 9.2-54 12.9-81.4 9.8z"
      />
    </svg>
  );
}

export function JewelersMutualLogo({ size = 18 }: LogoProps) {
  // Jewelers Mutual "JM" wordmark with the small ® mark trailing it. The
  // source uses a <symbol>/<use> wrapper which we flatten to direct paths.
  // Both paths are pure white in the source; rendered as currentColor so
  // they adapt to the surrounding text color.
  return (
    <svg
      role="img"
      aria-label="Jewelers Mutual"
      viewBox="0 0 52 37"
      fill="currentColor"
      width={size}
      height={size}
    >
      <path d="M46.1998 28.8279C45.7616 28.2366 45.4428 27.3623 45.247 26.2073C45.0509 25.0528 44.9528 23.5347 44.9528 21.6501V8.80019C44.9528 6.91561 45.0509 5.39753 45.247 4.24297C45.4432 3.08841 45.7616 2.21374 46.1998 1.62238C46.638 1.03102 47.2506 0.61246 48.0382 0.36926L47.9002 0.1875H38.061C38.061 0.64318 38.0304 1.03742 37.9688 1.36979C37.9077 1.70558 37.7943 2.1459 37.6287 2.69374C37.4632 3.23859 37.2118 4.04542 36.8811 5.10782L31.9843 20.2822L25.4088 0.187927H2.39686L2.26184 0.369687C3.01547 0.612887 3.6222 1.03187 4.07566 1.62281C4.52911 2.21417 4.84797 3.08841 5.02842 4.24339C5.20887 5.39838 5.29803 6.91603 5.29803 8.80062V27.393C5.29803 29.761 4.94266 31.6149 4.23488 32.9512C3.52412 34.2875 2.36586 35.2915 0.763062 35.9597L0.855196 36.1875C2.36586 36.1568 3.78779 35.9195 5.11758 35.4822C6.44738 35.0389 7.63621 34.4015 8.67813 33.5669C9.72006 32.7323 10.5289 31.7224 11.1051 30.5367C11.6782 29.351 11.9661 27.9993 11.9661 26.4813V8.80062C11.9661 6.91603 12.0642 5.88777 12.2603 4.73278C12.4565 3.57822 12.7813 2.70355 13.2348 2.10921C13.6882 1.51785 14.6168 1.14238 15.4439 1.12403C16.6267 1.12403 17.4262 1.51827 17.8801 2.10921C18.3336 2.70355 18.6524 3.57822 18.8329 4.73278C19.0138 5.88734 19.1055 6.91603 19.1055 8.80062V23.1528C19.1055 24.6125 18.998 25.8289 18.7866 26.8017C18.5752 27.7715 18.241 28.5229 17.7905 29.0558C17.3371 29.5887 16.7609 29.9304 16.0655 30.081L16.2034 30.2628H22.914L23.049 30.081C22.3841 29.93 21.8173 29.5882 21.3485 29.0558C20.8798 28.5229 20.5397 27.7719 20.3283 26.8017C20.1168 25.8285 20.0128 24.6125 20.0128 23.1528V3.83379L29.0827 30.9468H29.3523L38.2881 3.51337V21.6501C38.2881 23.5347 38.196 25.0528 38.0155 26.2073C37.8347 27.3619 37.5162 28.2366 37.0628 28.8279C36.6093 29.4193 36.0056 29.8378 35.249 30.081L35.384 30.2628H47.9011L48.0391 30.081C47.2515 29.8378 46.6388 29.4189 46.2006 28.8279" />
      <path d="M51.9036 2.10589C51.9036 1.17149 51.1521 0.41629 50.2219 0.41629C49.2916 0.41629 48.5405 1.17149 48.5405 2.10589C48.5405 3.04029 49.292 3.79592 50.2219 3.79592C51.1521 3.79976 51.9036 3.04072 51.9036 2.10589ZM51.5996 2.10589C51.5996 2.48776 51.4442 2.83464 51.1971 3.08637C50.9466 3.3381 50.6048 3.49085 50.2215 3.49085C49.8419 3.49085 49.4963 3.33469 49.2458 3.08637C48.9953 2.83464 48.8433 2.49117 48.8433 2.10589C48.8433 1.72445 48.9991 1.378 49.2458 1.12541C49.4963 0.873677 49.8381 0.721784 50.2215 0.721784C50.601 0.721784 50.9466 0.877944 51.1971 1.12541C51.4442 1.38098 51.5996 1.72445 51.5996 2.10589ZM50.9887 2.69341L50.6167 2.26248C50.8137 2.2164 50.9509 2.08669 50.9509 1.88061C50.9509 1.61778 50.7309 1.4804 50.4346 1.4804H49.6033V2.69H49.8767V2.27741H50.3127L50.6583 2.69H50.9887V2.69341ZM50.4197 2.05213H49.8771V1.71293H50.4197C50.5637 1.71293 50.6702 1.74749 50.6702 1.88061C50.6736 2.01842 50.5637 2.05213 50.4197 2.05213Z" />
    </svg>
  );
}

export function RingLogo({ size = 18 }: LogoProps) {
  // Official Ring wordmark — "Ring" letters with the amber dot over the "i".
  // The letterforms are white in the source SVG; rendered as currentColor so
  // they pick up the surrounding text color (works on dark backgrounds; will
  // need a non-white surface to be visible on light themes). The dot keeps
  // its brand amber.
  return (
    <svg
      role="img"
      aria-label="Ring"
      viewBox="0 0 1000 640.27"
      width={size}
      height={size}
    >
      <g fill="currentColor">
        <path d="m999.99,506.14V250.11s0-.08,0-.11c-.08-73.73-60.11-133.73-133.95-133.75-73.78.02-133.83,60.07-133.86,133.9v121.65c0,73.83,60.07,133.9,133.9,133.9,31.78,0,62.06-11.09,86.17-31.4v32.15c-.03,46.67-40.51,86.1-88.39,86.1-26.43,0-52.76-12.02-68.73-31.36-.05-.06-.1-.11-.14-.17-4.56-5.49-11.23-8.63-18.36-8.63-13.16,0-23.87,10.71-23.87,23.87,0,5.45,1.9,10.79,5.34,15.03.04.06.09.12.13.17,25.18,30.57,64.67,48.82,105.63,48.82,75.07,0,136.14-60.07,136.14-133.9,0-.08,0-.15,0-.22Zm-47.74-256v121.65c0,47.51-38.65,86.17-86.17,86.17s-86.17-38.65-86.17-86.2v-121.62c0-47.51,38.65-86.17,86.17-86.17s86.17,38.65,86.17,86.17Z" />
        <path d="m528.09,116.24c-73.89,0-134.04,60.1-134.1,133.98,0,.04,0,.08,0,.12v220.39c0,13.18,10.72,23.9,23.9,23.9s23.9-10.72,23.9-23.91v-220.39c0-47.58,38.71-86.29,86.29-86.29s86.29,38.71,86.29,86.29v220.39c0,13.18,10.72,23.9,23.9,23.9s23.9-10.72,23.9-23.9v-220.44c-.03-73.91-60.18-134.04-134.1-134.04Z" />
        <path d="m198.11,140.9c-.08-.06-.16-.12-.24-.18,0,0-.43-.29-.44-.3-22.57-15.8-49.1-24.16-76.39-24.16h-.03c-.12-.01-.31-.02-.43-.02h0C50.74,116.26.03,172.64.01,250c0,.11-.01.23-.01.34v220.39c0,13.18,10.72,23.9,23.9,23.9s23.9-10.72,23.9-23.9v-220.39c0-52.42,28.58-86.29,72.82-86.29,18,0,35.24,5.48,49.87,15.86.09.07.19.13.28.18,3.96,2.67,8.56,4.07,13.33,4.07,13.18,0,23.91-10.72,23.91-23.91,0-7.65-3.7-14.86-9.91-19.36Z" />
        <path d="m290.08,137.39c-13.15,0-23.85,10.7-23.85,23.85v309.6c0,13.15,10.7,23.85,23.85,23.85s23.85-10.7,23.85-23.85V161.24c0-13.15-10.7-23.85-23.85-23.85Z" />
      </g>
      <path
        fill="#FEA908"
        d="m289.69,0c-20.66,0-37.47,16.81-37.47,37.47s16.81,37.46,37.47,37.46,37.46-16.81,37.46-37.46S310.34,0,289.69,0Z"
      />
    </svg>
  );
}

export function OCWALogo({ size = 18 }: LogoProps) {
  // Onondaga County Water Authority — green-and-blue interlocking wave arcs.
  return (
    <svg
      role="img"
      aria-label="OCWA"
      viewBox="0 0 445.7 302.4"
      width={size}
      height={size}
    >
      <path
        fill="#25B351"
        d="M445.7,151.2c0,12.2-2.1,24-6.1,35.3l-128.1-11.9c4.2-7.3,6.5-15.2,6.5-23.4c0-35.6-42.6-64.5-95.1-64.5c-37,0-69.1,14.3-84.8,35.3L15.8,95.2C48.6,39.4,129,0,222.9,0C345.9,0,445.7,67.7,445.7,151.2z"
      />
      <path
        fill="#2F2CF7"
        d="M432.2,203.2C401,261.1,319,302.4,222.9,302.4C99.8,302.4,0,234.7,0,151.2c0-13.9,2.8-27.3,8-40.1l121.9,26.7c-1.3,4.3-2.1,8.8-2.1,13.4c0,35.7,42.6,64.6,95.1,64.6c30.6,0,57.8-9.8,75.2-25L432.2,203.2z"
      />
    </svg>
  );
}

export function TravelersLogo({ size = 18 }: LogoProps) {
  // Official Travelers Insurance umbrella in #CB2D2F. Two paths — the
  // arched canopy and the vertical handle. Source viewBox is offset
  // (441.3 404.5 …); preserved as authored.
  return (
    <svg
      role="img"
      aria-label="Travelers"
      viewBox="441.3 404.5 118.2 104.1"
      width={size}
      height={size}
    >
      <path
        fill="#CB2D2F"
        d="M441.8,443.7h-0.2c-0.2,0-0.4-0.2-0.4-0.4l0,0c0-0.1,0-0.1,0-0.2l0,0c14.3-32.6,52.4-47.5,85-33.2c14.8,6.5,26.7,18.4,33.2,33.2l0,0c0.1,0.2,0,0.4-0.2,0.5c0,0-0.1,0-0.1,0h-0.2l0,0c-5.5-3-11.8-4.5-18-4.4c-7.1-0.2-14.1,1.7-20,5.7l0,0c-0.1,0-0.2,0-0.2,0h-0.2l0,0c-12.3-7.6-27.8-7.6-40.1,0l0,0c-0.1,0-0.2,0-0.2,0h-0.2l0,0c-5.9-3.9-12.9-5.9-20-5.7C453.6,439.2,447.4,440.7,441.8,443.7L441.8,443.7z"
      />
      <path
        fill="#CB2D2F"
        d="M503.8,444.1L503.8,444.1c0-0.2-0.1-0.3-0.3-0.3l0,0c-1.1-0.1-2.1-0.2-3.2-0.2c-0.9,0-1.8,0-2.7,0l0,0c-0.2,0-0.3,0.1-0.3,0.3l0,0v52.8c0,3-2.4,5.4-5.4,5.4c-2.3,0-4.4-1.5-5.1-3.6l0,0c-0.7-1.7-2.6-2.4-4.2-1.8c-1.4,0.6-2.3,2.1-1.9,3.7l0,0c2,6.2,8.7,9.7,14.9,7.7c4.9-1.6,8.3-6.2,8.2-11.4L503.8,444.1z"
      />
    </svg>
  );
}

export function LemonadeLogo({ size = 18 }: LogoProps) {
  // Official Lemonade pink (#FF0083) — the stylized "L" lemon-pitcher mark.
  return (
    <svg
      role="img"
      aria-label="Lemonade"
      viewBox="0 0 33 44.6"
      width={size}
      height={size}
    >
      <path
        fill="#FF0083"
        fillRule="evenodd"
        d="M7.5,39.2c-4.4-.2-4.7-3.6-.6-4.5,3.7-.8,6.8.5,6.8.5,0,0-.9,4.2-6.2,4h0ZM27.1,4.3c2.6.2,2.4,3.8,1.7,6-2,6.6-6.9,6.8-6.9,6.8,0,0,1.5-13.1,5.2-12.8h0ZM21.5,20.2s8.2.1,11-9.6C34.1,4.9,31.8.1,27,0s-8.6,4.2-10.3,17.1c0,0-4.7-.6-6.7-4.3-1-1.8-2.5-.9-2,.7,1.6,5.6,8.4,6.5,8.4,6.5,0,0-.4,2.5-.8,5.6-.4,3-1.1,6.7-1.1,6.7,0,0-4.3-1.4-9-.4-7.1,1.5-7.9,10.3.8,11.2,10.2,1,12.1-6.1,12.1-6.1,0,0,2,1,4.8,3.5,3.2,2.8,4.6,4.6,5.2,4,.7-.7-.9-3.1-3.8-6.2-3.3-3.5-5.6-4.4-5.6-4.4,0,0,.5-2.3,1.1-6,.8-3.9,1.4-7.7,1.4-7.7Z"
      />
    </svg>
  );
}

export function WilliamPennLogo({ size = 18 }: LogoProps) {
  // William Penn Life Insurance shares the Legal & General quill/feather
  // mark — five overlapping color planes (red, blue, yellow, green) under a
  // dark outline silhouette. Inlined verbatim from the supplied source.
  return (
    <svg
      role="img"
      aria-label="William Penn"
      viewBox="0 0 206.7 201.8"
      width={size}
      height={size}
    >
      <path
        fill="#C50B30"
        d="M68.8,16.7c-.6-.8-1.5-1.2-2.4-1.2s-1,.1-1.4.4c-19.6,10.8-35.8,27.3-47,47.8C7.6,82.5,2.3,103.7,3.1,123.4c0,1.1.7,2.1,1.8,2.6.4.2.8.3,1.2.3.7,0,1.3-.2,1.9-.7,7.6-6.1,17.5-9.6,27.1-9.6s11.1,1.2,15.6,3.5c.4.2.9.3,1.4.3.7,0,1.4-.2,2-.7.9-.8,1.3-2.1.9-3.2-13.1-39.4-3.4-79.4,13.6-95.4,1.1-1,1.2-2.7.4-3.9h0Z"
      />
      <path
        fill="#005DBA"
        d="M109.4,94.1c0,0,0-.2-.1-.3L74.2,16.1c-.4-.9-1.2-1.6-2.1-1.7-.2,0-.5,0-.6,0-.8,0-1.6.3-2.1.9-10.9,11-17.8,27.3-20,47.1-1.9,17.5,0,36.5,5.3,53.5.1.5.4.9.7,1.2,0,0,.2.2.3.3.5.4,1.1.6,1.8.6s1.8-.4,2.4-1.2c10-13.3,27.1-19.3,39.6-19.3s4.5.2,6.5.6c.2,0,.4,0,.6,0,.9,0,1.8-.4,2.3-1.1.6-.8.8-1.8.5-2.8h0Z"
      />
      <path
        fill="#FFB600"
        d="M161.4,66.1s0,0,0-.1c-9.6-15-22.9-28.7-37.5-38.4-14.8-9.9-30.1-15.1-44.1-15.1s-3.2,0-4.9.2c-1,0-1.9.6-2.4,1.5-.5.9-.5,1.9,0,2.8l37,76.8c.5,1,1.5,1.7,2.6,1.7h0c1.1,0,2.1-.6,2.6-1.5,6.4-11.3,23.2-23.4,41.9-23.4s1.4,0,2.2,0c1.1,0,2-.6,2.6-1.5.5-.9.5-2.1,0-3h0Z"
      />
      <path
        fill="#00633D"
        d="M200.3,31.4c0,0-.1-.1-.2-.1C179.4,12.6,150.3,2,120.2,2s-29,2.6-42.1,7.6c-1.4.5-2.2,2.1-1.8,3.5.3,1.3,1.5,2.3,2.9,2.3s.3,0,.4,0c1.5-.2,3-.2,4.5-.2,22.6,0,56.5,17.6,77.3,51.2.5.9,1.5,1.4,2.6,1.4s.3,0,.5,0c1.2-.2,2.2-1.1,2.4-2.3,3.2-13.8,16.4-25.7,32-28.9,1.1-.2,2-1,2.3-2.2.3-1.1,0-2.2-.9-3h0Z"
      />
      <path
        fill="#FFFFFF"
        d="M205.2,31.6C175.2,3.6,125.9-10.1,77.7,8.6c-2.6,1-4.9.1-5.9-1.4l-4.1-5.9c-.9-1.4-2.8-.4-2.4,1.1l1.9,7c.5,1.6-.2,3.9-2.8,5.3C19.1,39.7-2.1,86.1.2,127.2H.2c.1,3.2,2.6,5.2,5.5,2.9,12.4-9.9,29.5-15.9,46.8-6.8,3.5,1.9,5.4.8,7.1-2,11-16.1,31.4-24,49.4-20.1l41.7,89.3c5,10.7,13.4,13.4,21.7,9.7,8.2-3.9,10.8-12.2,7-20.2l-3-6.5c-2.1-4.5-8.2-1.8-6,2.8l2.9,6.3c2.3,4.9.9,9.5-3.8,11.7s-9.3.9-12.7-6.4l-41.8-89.5c8.8-16.3,27.9-26.7,47.3-24.8,3.1.2,5.1-.4,6-4.3,4.1-19.2,19.7-28.2,35.3-31.5,3.6-.8,3.8-3.9,1.5-6.1h0ZM6.1,123.3c-1.4-37.9,20.1-82.7,60.4-104.9-22.2,20.8-25.1,66.4-14.4,98.5-14.4-7.3-33.2-3.8-45.9,6.4h0ZM57.5,114.9c-10-31.9-8.5-75,13.9-97.6l35.1,77.8c-14.9-2.9-37.1,4-49.1,19.8h0ZM112.1,92.4L75.1,15.7c31.8-2.8,65.8,23.8,83.7,52-19.7-1-39.3,11.7-46.7,24.8ZM163.9,64.7c-17.8-28.7-54.5-55.8-84.7-52.3,42.8-16.4,91-4.1,119.1,21.2-15.9,3.3-30.7,15.4-34.4,31.1Z"
      />
    </svg>
  );
}

export function VerizonLogo({ size = 18 }: LogoProps) {
  // Official Verizon checkmark — two stacked "V" strokes. The leading
  // stroke uses a radial gradient that ramps yellow → orange → red so the
  // mark reads with a heat-flare cast; the trailing stroke is solid Verizon
  // red. The gradient ID is generated per-instance via useId so multiple
  // VerizonLogos on the same page (calendar + dashboard bill card) don't
  // collide — SVG IDs are document-global and `url(#id)` resolves to the
  // first match, which broke the second instance when the first re-rendered.
  // useId returns ":r0:"-style strings; strip the colons since some SVG
  // url(#) parsers reject them.
  const gradId = `verizon-flare-${useId().replace(/:/g, '')}`;
  return (
    <svg
      role="img"
      aria-label="Verizon"
      viewBox="0 0 88.6 80.4"
      width={size}
      height={size}
    >
      <defs>
        <radialGradient
          id={gradId}
          cx="30"
          cy="80"
          r="80"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#f5ff1e" />
          <stop offset="0.3" stopColor="#f5ff1e" />
          <stop offset="0.5" stopColor="#f5a620" />
          <stop offset="0.7" stopColor="#f56321" />
          <stop offset="0.85" stopColor="#f53322" />
          <stop offset="1" stopColor="#f50a23" />
        </radialGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M55.2,79.4L24.7,0H0l30.5,79.4c.2.6.8,1,1.5,1h24.7c-.6,0-1.2-.4-1.5-1Z"
      />
      <path
        fill="#F50A23"
        d="M33.4,79.4L63.9,0h24.7l-30.5,79.4c-.2.6-.8,1-1.5,1h-24.7c.6,0,1.2-.4,1.5-1Z"
      />
    </svg>
  );
}

export function NationalGridLogo({ size = 18 }: LogoProps) {
  // Official National Grid mark — five interleaved diagonal slabs in navy,
  // green, cyan, red, and yellow. Fills are set on the wrapping <g> so the
  // five paths in each color share a single declaration.
  return (
    <svg
      role="img"
      aria-label="National Grid"
      viewBox="0 0 1520 1520"
      width={size}
      height={size}
    >
      <g fill="#00148C">
        <path d="m0 0h909.9c-109 109-217.9 218.2-327.1 327-14.1-13.8-28.5-27.4-41.9-42-180.4 180.1-360.4 360.7-540.9 540.7z" />
        <path d="m994.6 0h418.7c-346 346.9-692.7 693.3-1039.4 1039.6-14.3-13.6-28.5-27.3-42.2-41.4-105.3 104.8-210.1 210.1-315.4 315-5.7 5.7-11.1 11.7-16.3 17.9v-251.4c18.5-21.4 39.7-40.4 59.4-60.7 186.8-186.8 373.7-373.6 560.4-560.4 15.8-15.7 32.2-30.9 46.4-48.1-13.7-13.2-27.2-26.6-40.3-40.5 11.8-13.7 24.5-26.5 37.5-39.1 110.6-110.2 220.6-220.9 331.2-330.9z" />
        <path d="m1498.3 0h21.7v396.9c-49.8 52.2-102 102.2-152.6 153.6-23.1 22.5-45 46.1-68.8 67.7-13.2-14.1-26.9-27.5-40.8-40.8-73.9 70.5-144.7 144.2-217.5 216-242.1 242.2-484.5 484.2-726.4 726.6h-250.8c131-132.2 263.1-263.2 394.5-395.1-13.5-14.2-27.1-28.2-40.8-42.1 82-84.6 166.4-166.8 249.3-250.5 277.4-277.4 555-554.7 832.2-832.3z" />
        <path d="m1341.7 660.7c59.5-59.5 119.7-118.4 178.3-178.9v419.5c-110.4 112.2-222.8 222.5-333.5 334.4-14-14-28.2-27.9-42.1-42.1-109.1 108.6-217.7 217.6-326.5 326.4h-251.1c271.9-272.6 544.7-544.4 816.5-817-13.6-14.4-27.6-28.4-41.6-42.3z" />
        <path d="m1228.6 1277.9c97.1-97.2 194.9-193.9 291.4-291.8v533.9h-449.8c66.9-66.5 133.4-133.4 200.2-200q-20.7-21.2-41.8-42.1z" />
      </g>
      <path
        fill="#3DE02E"
        d="m909.9 0h84.7c-110.6 110-220.6 220.7-331.2 330.9-13 12.6-25.7 25.4-37.5 39.1 13.1 13.9 26.6 27.3 40.3 40.5-14.2 17.2-30.6 32.4-46.4 48.1-186.7 186.8-373.6 373.6-560.4 560.4-19.7 20.3-40.9 39.3-59.4 60.7v-254c180.5-180 360.5-360.6 540.9-540.7 13.4 14.6 27.8 28.2 41.9 42 109.2-108.8 218.1-218 327.1-327z"
      />
      <path
        fill="#00B0F0"
        d="m1413.3 0h85c-277.2 277.6-554.8 554.9-832.2 832.3-82.9 83.7-167.3 165.9-249.3 250.5 13.7 13.9 27.3 27.9 40.8 42.1-131.4 131.9-263.5 262.9-394.5 395.1h-63.1v-188.9c5.2-6.2 10.6-12.2 16.3-17.9 105.3-104.9 210.1-210.2 315.4-315 13.7 14.1 27.9 27.8 42.2 41.4 346.7-346.3 693.4-692.7 1039.4-1039.6z"
      />
      <path
        fill="#F53D33"
        d="m1367.4 550.5c50.6-51.4 102.8-101.4 152.6-153.6v84.9c-58.6 60.5-118.8 119.4-178.3 178.9 14 13.9 28 27.9 41.6 42.3-271.8 272.6-544.6 544.4-816.5 817h-252.9c241.9-242.4 484.3-484.4 726.4-726.6 72.8-71.8 143.6-145.5 217.5-216 13.9 13.3 27.6 26.7 40.8 40.8 23.8-21.6 45.7-45.2 68.8-67.7z"
      />
      <path
        fill="#FADB00"
        d="m1186.5 1235.7c110.7-111.9 223.1-222.2 333.5-334.4v84.8c-96.5 97.9-194.3 194.6-291.4 291.8q21.1 20.9 41.8 42.1c-66.8 66.6-133.3 133.5-200.2 200h-252.3c108.8-108.8 217.4-217.8 326.5-326.4 13.9 14.2 28.1 28.1 42.1 42.1z"
      />
    </svg>
  );
}

export function SpotifyLogo({ size = 18 }: LogoProps) {
  // Official Spotify mark — green disc with three white sound-wave arcs.
  // The source uses a vertical flip + 0.1 scale via nested transforms; both
  // are preserved verbatim so the path data renders correctly.
  return (
    <svg
      role="img"
      aria-label="Spotify"
      viewBox="0 0 226.73334 226.73334"
      width={size}
      height={size}
    >
      <g transform="matrix(1.3333333,0,0,-1.3333333,0,226.73333)">
        <g transform="scale(0.1)">
          <path
            fill="#1ED760"
            fillRule="nonzero"
            d="m 1345.62,945.281 c -269.94,160.309 -715.202,175.049 -972.893,96.839 -41.383,-12.56 -85.145,10.81 -97.68,52.18 -12.543,41.41 10.793,85.14 52.207,97.72 295.812,89.79 787.556,72.45 1098.316,-112.02 37.22,-22.1 49.43,-70.17 27.37,-107.328 -22.08,-37.219 -70.19,-49.488 -107.32,-27.391 z m -8.84,-237.441 c -18.94,-30.731 -59.12,-40.371 -89.81,-21.5 -225.05,138.336 -568.216,178.41 -834.466,97.59 -34.527,-10.434 -70.996,9.035 -81.484,43.496 -10.403,34.527 9.074,70.929 43.539,81.429 304.152,92.295 682.261,47.59 940.741,-111.253 30.69,-18.903 40.35,-59.102 21.48,-89.762 z M 1234.31,479.809 c -15.05,-24.68 -47.18,-32.411 -71.77,-17.368 -196.653,120.188 -444.173,147.329 -735.673,80.75 -28.09,-6.441 -56.086,11.161 -62.492,39.25 -6.434,28.079 11.102,56.079 39.254,62.489 318.996,72.925 592.633,41.539 813.361,-93.34 24.61,-15.031 32.36,-47.18 17.32,-71.781 z M 850.254,1687.68 c -462.496,0 -837.4337,-374.93 -837.4337,-837.418 0,-462.543 374.9377,-837.4417 837.4337,-837.4417 462.506,0 837.426,374.8987 837.426,837.4417 0,462.488 -374.92,837.418 -837.426,837.418"
          />
        </g>
      </g>
    </svg>
  );
}

export function AppleMusicLogo({ size = 18 }: LogoProps) {
  // Official Apple Music badge — white note on the brand-red (#FA243C) rounded
  // square. The simple-icons mark draws the square with the note as a
  // transparent cutout in a single path (so on its own the note shows whatever
  // is behind it). We lay a solid white rounded rect behind the note region
  // and paint the badge red on top; the cutout then reveals the white,
  // rendering the note white. Path data is the simple-icons `applemusic` glyph.
  return (
    <svg
      role="img"
      aria-label="Apple Music"
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="#FFFFFF" />
      <path
        fill="#FA243C"
        d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.045-1.773-.6-1.943-1.536a1.88 1.88 0 011.038-2.022c.323-.16.67-.25 1.018-.324.378-.082.758-.153 1.134-.24.274-.063.457-.23.51-.516a.904.904 0 00.02-.193c0-1.815 0-3.63-.002-5.443a.725.725 0 00-.026-.185c-.04-.15-.15-.243-.304-.234-.16.01-.318.035-.475.066-.76.15-1.52.303-2.28.456l-2.325.47-1.374.278c-.016.003-.032.01-.048.013-.277.077-.377.203-.39.49-.002.042 0 .086 0 .13-.002 2.602 0 5.204-.003 7.805 0 .42-.047.836-.215 1.227-.278.64-.77 1.04-1.434 1.233-.35.1-.71.16-1.075.172-.96.036-1.755-.6-1.92-1.544-.14-.812.23-1.685 1.154-2.075.357-.15.73-.232 1.108-.31.287-.06.575-.116.86-.177.383-.083.583-.323.6-.714v-.15c0-2.96 0-5.922.002-8.882 0-.123.013-.25.042-.37.07-.285.273-.448.546-.518.255-.066.515-.112.774-.165.733-.15 1.466-.296 2.2-.444l2.27-.46c.67-.134 1.34-.27 2.01-.403.22-.043.442-.088.663-.106.31-.025.523.17.554.482.008.073.012.148.012.223.002 1.91.002 3.822 0 5.732z"
      />
    </svg>
  );
}

export function YouTubeLogo({ size = 18 }: LogoProps) {
  // Official YouTube mark — red (#FF0000) rounded "play button" body with a
  // white (#FFFFFF) play triangle. The source supplied the body and triangle
  // as a single even-odd path (triangle cut as a hole); split here into two
  // solid paths so the triangle renders white on top of the red body rather
  // than as a transparent cutout.
  return (
    <svg
      role="img"
      aria-label="YouTube"
      viewBox="0 0 234.7 165.3"
      width={size}
      height={size}
    >
      <path
        fill="#FF0000"
        d="M229.8,25.8c-2.7-10.2-10.7-18.2-20.7-20.9C190.7,0,117.3,0,117.3,0,117.3,0,44,0,25.7,4.9,15.5,7.6,7.6,15.6,4.9,25.8,0,44.2,0,82.7,0,82.7c0,0,0,38.4,4.9,56.8,2.7,10.2,10.6,18.2,20.8,20.9,18.3,4.9,91.7,4.9,91.7,4.9,0,0,73.4,0,91.7-4.9,10.1-2.7,18-10.7,20.7-20.9,4.9-18.4,4.9-56.8,4.9-56.8,0,0,0-38.4-4.9-56.8Z"
      />
      <path fill="#FFFFFF" d="M93.3,117.6V47.8l61.3,34.9-61.3,34.9Z" />
    </svg>
  );
}

export function SquarespaceLogo({ size = 18 }: LogoProps) {
  // Official Squarespace looped-link mark. Brand color is black; on the
  // dark theme that's invisible, so render with currentColor so it
  // inherits the surrounding text color (same pattern as AppleLogo).
  return (
    <svg
      role="img"
      aria-label="Squarespace"
      viewBox="0 0 125 102"
      width={size}
      height={size}
      fill="currentColor"
      fillRule="evenodd"
    >
      <path d="M26.2,54L65.7,14c2.7-2.7,6.3-4.2,10.1-4.2s7.4,1.5,10.1,4.2l3.1,3.1,6.2-6.2-3.1-3.1c-4.3-4.4-10.1-6.8-16.2-6.8-6.1,0-11.9,2.4-16.2,6.8L20,47.8l6.2,6.2Z" />
      <path d="M79,27.3l-6.2-6.2-39.7,39.9c-5.6,5.6-14.6,5.6-20.2,0-5.6-5.6-5.6-14.7,0-20.3L47.2,6.2l-6.2-6.2L6.7,34.4c-9,9-9,23.7,0,32.8,4.3,4.4,10.1,6.8,16.3,6.8s11.9-2.4,16.3-6.8l39.7-39.9Z" />
      <path d="M118.3,34.8c-4.3-4.4-10.1-6.8-16.3-6.8-6.2,0-11.9,2.4-16.3,6.8l-39.7,39.9,6.2,6.2,39.7-39.9c5.6-5.6,14.6-5.6,20.2,0,2.7,2.7,4.2,6.3,4.2,10.2s-1.5,7.5-4.2,10.2l-34.2,34.4,6.2,6.2,34.2-34.4c9-9,9-23.7,0-32.8" />
      <path d="M98.8,48l-39.5,40c-5.5,5.6-14.6,5.6-20.1,0l-3.1-3.1-6.2,6.2,3.1,3.1c4.3,4.4,10.1,6.8,16.2,6.8,6.1,0,11.9-2.4,16.2-6.8l39.5-40-6.2-6.2Z" />
    </svg>
  );
}

export function ATTLogo({ size = 18 }: LogoProps) {
  // Official AT&T globe in #00A8E0 (sky blue) — concentric horizontal arcs
  // forming the spheroid mark.
  return (
    <svg
      role="img"
      aria-label="AT&T"
      viewBox="0 0 411.2 411.1"
      width={size}
      height={size}
    >
      <path
        fill="#00A8E0"
        d="M79.5,367.9c34.9,27,78.6,43.2,126.1,43.2s99.3-19.3,135.5-50.9c.4-.4.2-.6-.2-.4-16.2,10.8-62.4,34.5-135.2,34.5s-103.2-14.1-125.8-26.7c-.4-.2-.6.1-.3.4M219.6,378.4c50.6,0,106.2-13.8,139.5-41.1,9.1-7.4,17.8-17.3,25.5-30.6,4.5-7.7,8.8-16.8,12.4-25.7.2-.4-.1-.6-.4-.2-30.9,45.5-120.5,73.9-212.9,73.9s-135.7-20.9-163.2-60.8c-.3-.4-.5-.2-.4.2,25.6,54.5,103.5,84.3,199.6,84.3M164.3,287.9C59.1,287.9,9.4,238.9.4,205.5c-.1-.5-.4-.4-.4,0,0,11.3,1.1,25.8,3.1,35.4.9,4.7,4.7,12.1,10.3,17.9,25.5,26.6,89,63.8,199,63.8s184.2-49.9,191.2-66.4c5-11.7,7.6-33,7.6-50.8s-.1-7.8-.3-11.1c0-.5-.3-.6-.4,0-7.5,40.2-135.6,93.6-246.2,93.6M19.8,117.4c-6,12-12.7,32.2-14.7,42.6-.9,4.5-.5,6.6,1.1,10,12.6,26.8,76.4,69.6,225.2,69.6s161.3-22.3,172.7-63c2.1-7.5,2.2-15.4-.5-26.1-3-11.9-8.7-25.8-13.5-35.6-.2-.3-.4-.3-.4.1,1.8,53.4-147.1,87.8-222.2,87.8S18.4,170.4,18.4,129.4s.8-7.9,1.8-12c.1-.4-.2-.4-.4-.1M341.4,51.8c.9,1.4,1.3,2.8,1.3,4.7,0,22.8-69.9,63.2-181.1,63.2s-97.1-30.3-97.1-49.6,2.6-13.9,8.5-21.1c.3-.4,0-.6-.3-.3-10.6,9-20.4,19.1-28.9,30.1-4.1,5.2-6.6,9.8-6.6,12.5,0,40,100.2,68.9,193.9,68.9s144.4-32.6,144.4-61.2-4-16.2-14.2-27.8c-6.6-7.5-12.9-13.7-19.5-19.7-.3-.3-.5,0-.3.3M310.8,29C280,10.5,244.2,0,205.6,0s-75.7,10.9-106.6,29.8c-9.3,5.7-14.5,10.3-14.5,16.2,0,17.3,40.5,36,112.4,36s126.3-20.4,126.3-40.1-4.1-8-12.4-12.9"
      />
    </svg>
  );
}

export function DropboxLogo({ size = 18 }: LogoProps) {
  // Dropbox blue (#0061FF) — open-box mark from simple-icons.
  return <SiDropbox color="#0061FF" size={size} />;
}

export function MicrosoftLogo({ size = 18 }: LogoProps) {
  // Official Microsoft four-square mark — red, green, blue, and yellow
  // quadrants. Each quadrant is its own colored path.
  return (
    <svg
      role="img"
      aria-label="Microsoft"
      viewBox="0 0 2499.6 2500"
      width={size}
      height={size}
    >
      <path fill="#f1511b" d="m1187.9 1187.9h-1187.9v-1187.9h1187.9z" />
      <path fill="#80cc28" d="m2499.6 1187.9h-1188v-1187.9h1187.9v1187.9z" />
      <path fill="#00adef" d="m1187.9 2500h-1187.9v-1187.9h1187.9z" />
      <path fill="#fbbc09" d="m2499.6 2500h-1188v-1187.9h1187.9v1187.9z" />
    </svg>
  );
}

export function AnthropicLogo({ size = 18 }: LogoProps) {
  // Official Anthropic "A" wordmark. Source SVG fills with white; rendered
  // here as currentColor so it inherits the surrounding text color and works
  // on either light or dark backgrounds.
  return (
    <svg
      role="img"
      aria-label="Anthropic"
      viewBox="0 0 92.2 65"
      width={size}
      height={size}
    >
      <path
        fill="currentColor"
        d="M66.5,0H52.4l25.7,65h14.1L66.5,0z M25.7,0L0,65h14.4l5.3-13.6h26.9L51.8,65h14.4L40.5,0C40.5,0,25.7,0,25.7,0z M24.3,39.3l8.8-22.8l8.8,22.8H24.3z"
      />
    </svg>
  );
}

export function ChatGPTLogo({ size = 18 }: LogoProps) {
  // OpenAI ChatGPT knot mark. Source SVG fills with white; rendered here
  // as currentColor so it inherits the surrounding text color and works
  // on either light or dark backgrounds.
  return (
    <svg
      role="img"
      aria-label="ChatGPT"
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      <path
        fill="currentColor"
        d="M9.20509 8.76511V6.50545C9.20509 6.31513 9.27649 6.17234 9.44293 6.0773L13.9861 3.46088C14.6046 3.10413 15.342 2.93769 16.103 2.93769C18.9573 2.93769 20.7651 5.14983 20.7651 7.50454C20.7651 7.67098 20.7651 7.86129 20.7412 8.05161L16.0316 5.2924C15.7462 5.12596 15.4607 5.12596 15.1753 5.2924L9.20509 8.76511ZM19.8135 17.5659V12.1664C19.8135 11.8333 19.6708 11.5955 19.3854 11.429L13.4152 7.95633L15.3656 6.83833C15.5321 6.74328 15.6749 6.74328 15.8413 6.83833L20.3845 9.45474C21.6928 10.216 22.5728 11.8333 22.5728 13.4031C22.5728 15.2108 21.5025 16.8758 19.8135 17.5657V17.5659ZM7.80173 12.8088L5.8513 11.6671C5.68486 11.5721 5.61346 11.4293 5.61346 11.239V6.00613C5.61346 3.46111 7.56389 1.53433 10.2042 1.53433C11.2033 1.53433 12.1307 1.86743 12.9159 2.46202L8.2301 5.17371C7.94475 5.34015 7.80195 5.57798 7.80195 5.91109V12.809L7.80173 12.8088ZM12 15.2349L9.20509 13.6651V10.3351L12 8.76534L14.7947 10.3351V13.6651L12 15.2349ZM13.7958 22.4659C12.7967 22.4659 11.8693 22.1328 11.0841 21.5382L15.7699 18.8265C16.0553 18.6601 16.198 18.4222 16.198 18.0891V11.1912L18.1723 12.3329C18.3388 12.4279 18.4102 12.5707 18.4102 12.761V17.9939C18.4102 20.5389 16.4359 22.4657 13.7958 22.4657V22.4659ZM8.15848 17.1617L3.61528 14.5452C2.30696 13.784 1.42701 12.1667 1.42701 10.5969C1.42701 8.76534 2.52115 7.12414 4.20987 6.43428V11.8574C4.20987 12.1905 4.35266 12.4284 4.63802 12.5948L10.5846 16.0436L8.63415 17.1617C8.46771 17.2567 8.32492 17.2567 8.15848 17.1617ZM7.897 21.0625C5.20919 21.0625 3.23488 19.0407 3.23488 16.5432C3.23488 16.3529 3.25875 16.1626 3.2824 15.9723L7.96817 18.6839C8.25352 18.8504 8.53911 18.8504 8.82446 18.6839L14.7947 15.2351V17.4948C14.7947 17.6851 14.7233 17.8279 14.5568 17.9229L10.0136 20.5393C9.39518 20.8961 8.6578 21.0625 7.89677 21.0625H7.897ZM13.7958 23.8929C16.6739 23.8929 19.0762 21.8474 19.6235 19.1357C22.2874 18.4459 24 15.9484 24 13.4034C24 11.7383 23.2865 10.121 22.002 8.95542C22.121 8.45588 22.1924 7.95633 22.1924 7.45702C22.1924 4.0557 19.4331 1.51045 16.2458 1.51045C15.6037 1.51045 14.9852 1.60549 14.3668 1.81968C13.2963 0.773071 11.8215 0.107086 10.2042 0.107086C7.32606 0.107086 4.92383 2.15256 4.37653 4.86425C1.7126 5.55411 0 8.05161 0 10.5966C0 12.2617 0.713506 13.879 1.99795 15.0446C1.87904 15.5441 1.80764 16.0436 1.80764 16.543C1.80764 19.9443 4.56685 22.4895 7.75421 22.4895C8.39632 22.4895 9.01478 22.3945 9.63324 22.1803C10.7035 23.2269 12.1783 23.8929 13.7958 23.8929Z"
      />
    </svg>
  );
}

export function AppleLogo({ size = 18 }: LogoProps) {
  // Apple's bitten-apple silhouette. Brand color is technically black, but on
  // the dark theme that's invisible — render as currentColor so it inherits
  // the surrounding text color (white in nav-style contexts, neutral
  // elsewhere). Used for "Apple Card" link rows.
  return <SiApple color="currentColor" size={size} />;
}

export function DiscoverLogo({ size = 18 }: LogoProps) {
  // Official Discover orb — orange linear-gradient circle with a radial
  // gradient overlay for the highlight. Source viewBox is offset; preserved
  // as authored. Gradient IDs are prefixed to avoid collisions when multiple
  // instances render on the same page.
  return (
    <svg
      role="img"
      aria-label="Discover"
      viewBox="-750.3 523.6 491.1 491.1"
      width={size}
      height={size}
    >
      <defs>
        <linearGradient
          id="discover-base"
          gradientUnits="userSpaceOnUse"
          x1="-2054.0183"
          y1="28.5911"
          x2="-2058.9595"
          y2="20.8541"
          gradientTransform="matrix(34.425 0 0 34.425 70239.7344 -18.0557)"
        >
          <stop offset="0" stopColor="#F9A020" />
          <stop offset="0.2041" stopColor="#F99D20" />
          <stop offset="0.4348" stopColor="#F79320" />
          <stop offset="0.6196" stopColor="#F68820" />
          <stop offset="0.7969" stopColor="#F57E20" />
          <stop offset="1" stopColor="#F47521" />
        </linearGradient>
        <radialGradient
          id="discover-glow"
          cx="-2137.646"
          cy="31.6172"
          r="12.5679"
          gradientTransform="matrix(-16.0595 -30.4495 23.6175 -12.4562 -35573.4258 -63749.4219)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#FF932C" />
          <stop offset="0.5677" stopColor="#E86D20" />
          <stop offset="0.8604" stopColor="#E85718" stopOpacity="0.8653" />
          <stop offset="1" stopColor="#640000" />
        </radialGradient>
      </defs>
      <g transform="translate(90.984375, 0.140625)">
        <circle cx="-595.8" cy="769" r="245.5" fill="url(#discover-base)" />
        <path
          d="M-595.8,1014.5c135.6,0,245.5-109.9,245.5-245.5s-109.9-245.5-245.5-245.5S-841.3,633.4-841.3,769S-731.4,1014.5-595.8,1014.5z"
          fill="url(#discover-glow)"
          opacity="0.65"
        />
      </g>
    </svg>
  );
}

export function FordLogo({ size = 18 }: LogoProps) {
  // Official Ford script wordmark in #047AF2.
  return (
    <svg role="img" aria-label="Ford" viewBox="0 0 126.7 50" width={size} height={size}>
      <g fill="#047AF2">
        <path d="M105.7,35.3c0,.2.3.4.5.5,0,0,0,0,0,.3-.7,1.5-1.3,2.3-2.1,3.5-.8,1.2-1.7,2.1-2.7,2.9-1.6,1.3-3.8,2.8-5.6,1.8-.8-.4-1.2-1.6-1.1-2.5,0-2.6,1.2-5.3,3.3-7.9,2.6-3.2,5.5-4.9,7.4-4,2,.9,1.3,3.2.5,5-.2.1-.2.3-.2.4M126.7,15.1c0-.2,0-.4-.3-.4h-5.5c-.4,0-.6.1-.8.4-.6,1.2-8.3,12.7-9.7,14.9-.2.3-.5.3-.6-.1-.2-1.3-1.3-2.8-3.1-3.6-1.4-.5-2.7-.7-4.1-.5-2.5.3-4.7,1.7-6.7,3.2-3,2.3-5.5,5.2-8.8,7.3-1.8,1.2-4.3,2.2-5.9.7-1.5-1.4-1.3-4.5,1-6.9.2-.2.5,0,.5.2-.2,1.1,0,2.1.9,2.9,1,.9,2.4,1,3.7.4,1.5-.7,2.3-2.1,2.5-3.7.4-2.4-1.5-4.6-3.8-4.7-1.9-.1-3.7.5-5.5,2-.9.7-1.4,1.3-2.2,2.4-.2.3-.5.3-.5-.1,0-3.1-1.2-4.9-3.8-4.9-2-.1-4.1,1-5.8,2.4-1.8,1.5-3.4,3.4-5.1,5.2-.2.2-.4.2-.5-.2,0-2.1-.6-4.1-1.5-5.5-.3-.5-1.1-.7-1.6-.4-.3.2-1.2.6-1.9,1.1-.3.3-.5.7-.3,1.2.9,3.1.7,6.6-.5,9.5-1.1,2.7-3.4,5.2-6,6-1.7.5-3.5.3-4.6-1.2-1.5-2.1-.9-5.8,1.4-8.8,2-2.7,4.9-4.4,7.8-5.5.3-.1.4-.4.3-.6-.2-.5-.4-1.1-.5-1.4-.3-.7-1-.8-1.9-.7-2.1.2-4,1-5.9,2-4.9,2.6-7.4,7.7-8.5,10.4-.5,1.3-1,2.1-1.5,2.8-.8,1-1.8,1.9-3.5,3.4-.2.2-.3.4-.2.7.2.4,1.1,1.7,1.4,1.8.3.2.6,0,.8-.2,1.2-.8,2.7-2.2,3.5-3.1.3-.3.5-.2.7.3.7,2.4,2.4,4.4,4.9,5.2,4.5,1.5,9.2-.7,12.7-4.5,2.2-2.4,3.1-4,3.9-5.1,1.5-1.8,4.3-5.8,7.7-8.4,1.3-1,2.7-1.7,3.5-1.2.6.4.8,1.3-.2,3-3.5,6.4-8.6,13.8-9.5,15.7-.2.3,0,.6.3.6h5.3c.3,0,.4,0,.5-.3,2.7-4.2,5.2-8.2,7.9-12.3.2-.2.3,0,.3,0,0,1,.3,2.3.9,3.1,1,1.6,2.5,2.3,4.1,2.3s1.9-.2,3.3-.7c1.1-.4,2-1,2.7-1.5.4-.3.5.2.5.3-.6,3.3.2,7.3,3.4,8.9,4,1.9,8.3-.8,10.8-3.3.2-.2.5-.2.5.3,0,.9.5,2.1,1.3,2.8,2.1,2.1,6.3,1.3,10.6-2,2.7-2.1,5.6-5.1,8.1-8.2,0,0,.2-.3,0-.5-.4-.4-1-.9-1.4-1.2-.2-.2-.4,0-.6,0-2.6,2.5-5,5.4-8.4,7.7-1.1.8-3,1.5-3.8.3-.3-.4-.3-1,0-1.8.6-2.5,16.5-26.6,17.3-28" />
        <path d="M52.2,18c0,.4.2.4.4.4.8-.2,1.7-.8,2.2-1.6.5-.7.7-1.7.2-2.1-.4-.4-1.1-.3-1.6.2-1.1.8-1.4,2.1-1.2,3.1M11.8,49c-5.9,0-11.1-3.6-11.7-9.9-.3-3.5.7-6.6,2.6-9.2,1.8-2.3,4.7-4.2,7.3-4.7.8-.2,1.9-.1,2.4.5.7.8.5,1.8-.2,2.1-1.9,1-4.2,2.7-5.4,5-.9,1.9-1.1,4.1.3,6.3,2.4,3.6,9,3.7,15.1-2,1.5-1.4,2.9-2.7,4.3-4.3,2.7-3.1,3.6-4.2,7.2-9.2,0-.1,0-.1,0-.2,0-.1,0-.1-.2-.1-2.4,0-4.5.7-6.5,2.2-.6.4-1.4.4-1.8-.2s-.1-1.5.6-1.9c3.3-2.3,6.9-2.7,10.5-3.3,0,0,.2,0,.5-.3,1.5-1.9,2.6-3.7,4.2-5.6,1.6-2,3.7-3.8,5.3-5.2,2.2-1.9,3.6-2.5,5.1-3.3.4-.2,0-.4-.2-.4-8.1-1.5-16.5-3.5-24.5-.5-5.6,2.1-8,6.5-6.8,9.4.8,2.1,3.6,2.4,6.2,1.2,2.1-1.1,4.1-2.7,5.5-4.9.8-1.2,2.4-.3,1.5,1.8-2.4,5.5-6.8,9.9-12.3,10.1-4.7.2-7.6-3.3-7.6-7.5C13.2,6.6,22.5,1.1,32.2.3c11.7-1,22.8,2.1,34.5,3.6,8.9,1.1,17.5,1.1,26.4-1,1.1-.2,1.8.5,1.7,1.5,0,1.5-1.3,3-4.2,4.7-3.4,1.8-6.9,2.4-10.5,2.4-9,0-17.2-3.5-25.8-5.3,0,.4,0,.8-.2,1.1-4.7,3.5-9,7-11.9,12,0,.1,0,.2,0,.2,2.5-.1,4.7-.2,7.1-.4.4,0,.4-.1.4-.4-.2-1.7.3-3.7,1.8-5,1.5-1.4,3.5-1.5,4.8-.5,1.5,1.1,1.3,3.2.5,4.7-.8,1.6-2.1,2.4-3.4,2.8,0,0-.3.1-.2.3,0,.4,1.8,1.4,2,1.6s0,.6-.2.9-.5.4-.8.4-.5-.1-.9-.3c-.8-.4-1.8-1.1-2.4-2-.2-.3-.3-.3-.9-.3-2.5.2-6.4.5-9.1.8-.6.1-.7.2-.9.4-4.2,7-8.8,14.5-13.7,19.6-5.5,5.4-9.9,6.9-14.6,6.9" />
      </g>
    </svg>
  );
}

export function SoFiLogo({ size = 18 }: LogoProps) {
  // Official SoFi 3x3 dot mark in #00A2C7. Filled disc / open ring pattern.
  return (
    <svg role="img" aria-label="SoFi" viewBox="0 0 40 40" width={size} height={size}>
      <g fill="#00A2C7">
        <circle cx="6.3" cy="6.3" r="6.3" />
        <circle cx="20" cy="6.3" r="6.3" />
        <path d="M33.7,1.9c2.5,0,4.5,2,4.5,4.5s-2,4.5-4.5,4.5-4.5-2-4.5-4.5,2-4.5,4.5-4.5M33.7,0C30.2,0,27.3,2.8,27.3,6.3s2.8,6.3,6.3,6.3,6.3-2.8,6.3-6.3S37.2,0,33.7,0h0Z" />
        <circle cx="6.3" cy="20" r="6.3" />
        <path d="M20,15.5c2.5,0,4.5,2,4.5,4.5s-2,4.5-4.5,4.5-4.5-2-4.5-4.5,2-4.5,4.5-4.5M20,13.7c-3.5,0-6.3,2.8-6.3,6.3s2.8,6.3,6.3,6.3,6.3-2.8,6.3-6.3-2.8-6.3-6.3-6.3h0Z" />
        <circle cx="33.7" cy="20" r="6.3" />
        <path d="M6.3,29.2c2.5,0,4.5,2,4.5,4.5s-2,4.5-4.5,4.5-4.5-2-4.5-4.5,2-4.5,4.5-4.5M6.3,27.3C2.8,27.3,0,30.2,0,33.7s2.8,6.3,6.3,6.3,6.3-2.8,6.3-6.3-2.8-6.3-6.3-6.3h0Z" />
        <circle cx="20" cy="33.7" r="6.3" />
        <circle cx="33.7" cy="33.7" r="6.3" />
      </g>
    </svg>
  );
}

export function LedgerLogo({ size = 18 }: LogoProps) {
  // Official Ledger bracket mark. Source SVG is monochrome — render in the
  // app's primary text color so it works on dark or light backgrounds.
  return (
    <svg role="img" aria-label="Ledger" viewBox="0 0 24 24" width={size} height={size}>
      <path
        fill="currentColor"
        d="M0,16.7v6.1h9v-1.4H1.3v-4.8H0ZM22.7,16.7v4.8h-7.7v1.4h9v-6.1h-1.3ZM9,7.3v9.3h5.9v-1.2h-4.6V7.3h-1.3ZM0,1.2v6.1h1.3V2.6h7.7v-1.4H0ZM15,1.2v1.4h7.7v4.8h1.3V1.2h-9Z"
      />
    </svg>
  );
}

export function WebullLogo({ size = 18 }: LogoProps) {
  // Official Webull arch mark in #0043E9. The source path is in a 121.9 × 65
  // viewBox (wide); we recenter it into a 24×24 box so it aligns with the
  // other circular/square broker marks. The horn shape ends up smaller than
  // a circle visually but stays recognizable.
  return (
    <svg role="img" aria-label="Webull" viewBox="0 0 24 24" width={size} height={size}>
      <g transform="translate(0, 5.6) scale(0.197)">
        <path
          fill="#0043E9"
          d="M6.5,0.5C6.7,0.2,7.1,0,7.6,0c0.7,0,1.3,0.6,1.3,1.3c0,0.1,0,0.2,0,0.3c0,0,0,0,0,0c0,0,0,0,0,0C4,18.9,27.2,33,61,33c33.8,0,56.9-14,52.2-31.4l0,0c0-0.1-0.1-0.3-0.1-0.4c0-0.7,0.5-1.2,1.2-1.2c0.5,0,0.9,0.3,1.1,0.7c4,5.5,6.3,11.8,6.4,18.4c0.3,23.2-27,45.9-60.9,45.9S-0.3,42.2,0,19.1C0.1,12.4,2.4,6.1,6.5,0.5z"
        />
      </g>
    </svg>
  );
}

export function BitmartLogo({ size = 18 }: LogoProps) {
  // Official BitMart mark — multi-color teal stacked-bars / arch design.
  // Source viewBox is 233.91 × 281; recentered into 24×24 with a transform.
  return (
    <svg role="img" aria-label="BitMart" viewBox="0 0 24 24" width={size} height={size}>
      <g transform="translate(2, 0) scale(0.0854)">
        <path d="m202 136.19a84.68 84.68 0 0 0 18.25-52.65c0-47-38.08-83.54-85.06-83.54h-103.29v10.63h101c41.52 0 75.18 32.15 75.18 73.67a74.88 74.88 0 0 1 -14.8 44.81z" fill="#55a49f" />
        <path d="m190.48 127.39a61.34 61.34 0 0 0 17.61-43.09 61.52 61.52 0 0 0 -61.51-61.52h-87.34v10.64h89.61a50.12 50.12 0 0 1 50.15 50.12 50 50 0 0 1 -17.86 38.36z" fill="#4a8c89" />
        <path d="m173.63 119.37c14.37-6.28 25.37-19.96 25.37-35.83 0-21.81-20.72-39.49-42.53-39.49h-75.97v6.07h75.19c18 0 35.69 14.63 35.69 32.66 0 14.61-11.58 27-25.57 31.15z" fill="#5aaba5" />
        <path d="m1.52 270.37h16.71v10.63h-16.71z" fill="#4a8c8a" />
        <path d="m18.23 249.1h66.83v7.59h-66.83z" fill="#5aaba6" />
        <path d="m18.23 202.02h71.39v6.08h-71.39z" fill="#4b8f8e" />
        <path d="m103.29 202.02h45.57v6.08h-45.57z" fill="#7cc6c7" />
        <path d="m31.9 180.75h24.3v6.08h-24.3z" fill="#5ab5b4" />
        <path d="m69.87 180.75h78.98v6.08h-78.98z" fill="#428989" />
        <path d="m95.69 85.06h51.64v6.08h-51.64z" fill="#549391" />
        <path d="m34.94 44.05h28.86v6.08h-28.86z" fill="#306563" />
        <path d="m42.53 66.83h106.32v6.08h-106.32z" fill="#64b9b9" />
        <path d="m15.19 85.06h65.31v6.08h-65.31z" fill="#7fd6d5" />
        <path d="m33.42 268.85v10.63h115.43c47 0 85.06-36.56 85.06-83.54a85.06 85.06 0 0 0 -85.06-85.06h-74.42v9.12h72.15a75.19 75.19 0 0 1 75.18 75.19c0 41.53-33.66 73.67-75.18 73.67z" fill="#55a49f" />
        <path d="m51.64 133.66v10.64h110.88a50.12 50.12 0 0 1 50.13 50.12 50.13 50.13 0 0 1 -50.13 50.13h-65.31v12.15h63a61.51 61.51 0 0 0 61.51-61.52 61.52 61.52 0 0 0 -61.51-61.52z" fill="#4a8c89" />
        <path d="m83.54 156.45v6.07h85.82c18 0 35.69 14.63 35.69 32.66 0 18-17.65 32.66-35.69 32.66h-169.36v7.59h170.12c21.81 0 42.53-17.68 42.53-39.49 0-21.81-20.72-39.49-42.53-39.49z" fill="#5aaba5" />
      </g>
    </svg>
  );
}

export function PayPalLogo({ size = 18 }: LogoProps) {
  // Official PayPal interlocking-P mark in the three-blue palette.
  return (
    <svg role="img" aria-label="PayPal" viewBox="0 0 48 48" width={size} height={size}>
      <path
        fill="#001C64"
        d="M37.972 13.82c.107-5.565-4.485-9.837-10.799-9.837H14.115a1.278 1.278 0 0 0-1.262 1.079L7.62 37.758a1.038 1.038 0 0 0 1.025 1.2h7.737l-1.21 7.572a1.038 1.038 0 0 0 1.026 1.2H22.5c.305 0 .576-.11.807-.307.231-.198.269-.471.316-.772l1.85-10.885c.047-.3.2-.69.432-.888.231-.198.433-.306.737-.307H30.5c6.183 0 11.43-4.394 12.389-10.507.678-4.34-1.182-8.287-4.916-10.244Z"
      />
      <path
        fill="#0070E0"
        d="m18.056 26.9-1.927 12.22-1.21 7.664a1.038 1.038 0 0 0 1.026 1.2h6.67a1.278 1.278 0 0 0 1.261-1.079l1.758-11.14a1.277 1.277 0 0 1 1.261-1.078h3.927c6.183 0 11.429-4.51 12.388-10.623.68-4.339-1.504-8.286-5.238-10.244-.01.462-.05.923-.121 1.38-.959 6.112-6.206 10.623-12.389 10.623h-6.145a1.277 1.277 0 0 0-1.261 1.077Z"
      />
      <path
        fill="#003087"
        d="M16.128 39.12h-7.76a1.037 1.037 0 0 1-1.025-1.2l5.232-33.182a1.277 1.277 0 0 1 1.262-1.078h13.337c6.313 0 10.905 4.595 10.798 10.16-1.571-.824-3.417-1.295-5.44-1.295H21.413a1.278 1.278 0 0 0-1.261 1.078L18.057 26.9l-1.93 12.22Z"
      />
    </svg>
  );
}

export function BluevineLogo({ size = 18 }: LogoProps) {
  // Official Bluevine mark in #1943DC.
  return (
    <svg
      role="img"
      aria-label="Bluevine"
      viewBox="0 0 129.2 179.7"
      width={size}
      height={size}
    >
      <path
        fill="#1943DC"
        d="M70.2,61.7v118c32.6,0,59-26.4,59-59S102.7,61.7,70.2,61.7z M52.4,179.6c2.2,0.1,4.4,0.2,6.7,0.1c0.4-2.2,0.7-4.4,0.9-6.6c2.2-31-21.1-57.9-52.1-60.1c-2.3-0.1-4.6-0.2-6.8-0.1c-0.4,2.2-0.7,4.4-0.9,6.6C-2.1,150.5,21.3,177.5,52.4,179.6z M59.3,118V0h-59v59C0.3,91.6,26.7,118,59.3,118z"
      />
    </svg>
  );
}

export function CitiLogo({ size = 18 }: LogoProps) {
  // Official Citi mark — blue "Citi" wordmark with red arc above.
  return (
    <svg role="img" aria-label="Citi" viewBox="0 0 89 89" width={size} height={size}>
      <defs>
        <clipPath id="citi-clip">
          <rect
            width="56"
            height="32.3599"
            fill="#FFFFFF"
            transform="translate(16.5 28.3201)"
          />
        </clipPath>
      </defs>
      <g clipPath="url(#citi-clip)">
        <path
          fill="#255BE3"
          d="M16.5485 50.2744C16.5485 44.5 21.2765 40.0045 27.4384 40.0045C31.0038 40.0045 34.2592 41.5934 36.1194 43.9962L33.4453 46.6702C32.7361 45.7621 31.8311 45.0257 30.7978 44.5157C29.7645 44.0058 28.6294 43.7355 27.4772 43.7249C23.7955 43.7249 20.8502 46.4765 20.8502 50.2744C20.8502 54.1111 23.7955 56.8626 27.4772 56.8626C28.6707 56.8612 29.8477 56.5841 30.9165 56.0531C31.9853 55.522 32.9171 54.7513 33.6391 53.801L36.2744 56.3976C34.4917 58.9166 31.0813 60.583 27.4384 60.583C21.2765 60.583 16.5485 56.0875 16.5485 50.2744Z"
        />
        <path fill="#255BE3" d="M39.7623 40.7021H43.9865V59.8855H39.7623V40.7021Z" />
        <path
          fill="#255BE3"
          d="M51.8537 54.7311V44.2675H47.2419V40.7021H52.0474V36.5941L56.0004 34.6564V40.7021H62.2786V44.2675H56.0004V54.0336C56.0004 55.9713 57.0855 56.8239 59.1395 56.8239C60.2082 56.8294 61.2656 56.6047 62.2398 56.165V59.808C61.0192 60.3435 59.697 60.608 58.3644 60.583C54.6052 60.583 51.8537 58.5291 51.8537 54.7311Z"
        />
        <path fill="#255BE3" d="M65.6115 40.7021H69.8357V59.8855H65.6115V40.7021Z" />
        <path
          fill="#FF3C28"
          d="M54.7602 28.4945C58.2295 28.4862 61.6499 29.3129 64.7324 30.9047C67.815 32.4966 70.4692 34.8069 72.471 37.6405H67.5492C65.9359 35.8474 63.9637 34.4136 61.7605 33.4321C59.5572 32.4506 57.1722 31.9434 54.7602 31.9434C52.3483 31.9434 49.9632 32.4506 47.76 33.4321C45.5567 34.4136 43.5845 35.8474 41.9713 37.6405H37.0495C39.0512 34.8069 41.7055 32.4966 44.788 30.9047C47.8706 29.3129 51.2909 28.4862 54.7602 28.4945Z"
        />
      </g>
    </svg>
  );
}

export function GoldmanSachsLogo({ size = 18 }: LogoProps) {
  // Official Goldman Sachs script "G" mark. Source SVG fills with white;
  // only reads on darker backgrounds. Tall aspect (~0.35:1).
  return (
    <svg
      role="img"
      aria-label="Goldman Sachs"
      viewBox="0 0 35.806 101.8828"
      width={size}
      height={size}
    >
      <path
        fill="#FFFFFF"
        d="M35.806,86.1781c0,9.3623-6.8398,15.7048-17.6507,15.7048H2.1622V85.169h2.4433c2.2343,9.8668,6.3424,14.3354,13.1894,14.3354c5.6217,0,8.5767-3.0271,8.5767-7.9281c0-5.1821-3.1712-7.5605-7.856-10.2993l-7.0632-4.1803c-5.9749-3.5316-9.3623-7.7839-9.3623-13.838c0-6.6812,3.928-10.9768,10.1551-12.8363C3.2145,48.0801,0,40.0151,0,25.5788C0,9.2182,6.8398,0,20.6778,0H33.579v13.8309h-2.5226c-2.955-8.793-5.5497-11.5317-10.3065-11.5317c-6.847,0-8.793,7.3515-8.793,23.7121c0,18.8039,1.6577,22.9841,6.847,22.9841c3.8919,0,5.8379-2.4505,5.8379-8.3605v-7.2073c0-4.3893-1.4415-5.9749-5.9821-6.6956v-2.3064h15.4237v39.3376h-2.3064c-3.4595-9.8668-8.0002-12.1011-13.0453-12.1011c-4.901,0-7.9281,2.3064-7.9281,6.847c0,4.2451,2.5226,6.1911,7.2073,9.002l7.7119,4.6127C31.8492,75.7995,35.806,79.259,35.806,86.1781z"
      />
    </svg>
  );
}

export function BankOfAmericaLogo({ size = 18 }: LogoProps) {
  // Official Bank of America flag mark — interleaved red (#E31837) and
  // navy (#012169) waves. Wide aspect (~1.85:1).
  return (
    <svg
      role="img"
      aria-label="Bank of America"
      viewBox="0 0 133 72.1"
      width={size}
      height={size}
    >
      <path
        fill="#E31837"
        d="M77.6,69.1c0-1.6,1.3-2.9,3-2.9s3,1.3,3,2.9s-1.3,3-3,3S77.6,70.8,77.6,69.1 M78.2,69.1c0,1.3,1.1,2.4,2.5,2.4c1.3,0,2.4-1,2.4-2.4c0-1.3-1.1-2.3-2.4-2.3C79.2,66.8,78.2,67.9,78.2,69.1"
      />
      <path
        fill="#E31837"
        d="M82.1,70.8h-0.6l-1-1.5h-0.6v1.5h-0.4v-3.3H81c0.3,0,0.6,0,0.8,0.2c0.3,0.1,0.4,0.4,0.4,0.7c0,0.6-0.5,0.9-1.1,0.9L82.1,70.8L82.1,70.8z M80.5,68.9c0.5,0,1.1,0.1,1.1-0.5c0-0.4-0.4-0.5-0.8-0.5h-0.9v1H80.5L80.5,68.9z"
      />
      <path
        fill="#E31837"
        d="M66.3,72.1c21-18,50.5-35.5,66.7-41.8c-2.5-1.6-6.4-3.9-10.8-6.4c-20.9,6.9-45.3,22.2-67.1,39.5C58.8,66.2,62.7,69.1,66.3,72.1L66.3,72.1z"
      />
      <path
        fill="#012169"
        d="M56.7,23.2c-3.1-1.5-6.5-2.9-9.1-4.1c-7.9,3.9-18.2,9.8-31.2,19.1c2.8,1.5,5.8,3.3,9,5.1C35.4,35.9,45.5,28.9,56.7,23.2z"
      />
      <path
        fill="#E31837"
        d="M75.4,14.6c-3.8-2.1-17-7-26.1-9.1c-2.7,1-6.5,2.5-9.1,3.6c3.3,0.9,15.6,4.2,26.1,9.5C69,17.3,72.8,15.7,75.4,14.6z"
      />
      <path
        fill="#012169"
        d="M30.7,13.3C19,18.7,6.7,26.1,0,30.4c2.4,1.1,4.8,2,8.1,3.6c14.8-10,26.4-16.1,31-18.1C35.8,14.7,32.7,13.9,30.7,13.3L30.7,13.3z"
      />
      <path
        fill="#E31837"
        d="M84.2,11.5c2.7-0.9,5.8-1.7,8.5-2.5c-7.8-3.3-17.6-6.8-26.4-9c-1.4,0.4-5.6,1.5-8.5,2.4C60.8,3.3,70.7,5.6,84.2,11.5z M34.9,49.1c3.2,1.9,6.6,4.5,9.9,6.7c21.9-17,43.5-30.1,67.2-37.5c-3.3-1.7-6.2-3.2-9.9-5C87.9,16.9,63.6,26.6,34.9,49.1z"
      />
    </svg>
  );
}

export function SynchronyLogo({ size = 18 }: LogoProps) {
  // Official Synchrony three-bar mark in #FAC610 (gold).
  return (
    <svg
      role="img"
      aria-label="Synchrony"
      viewBox="0 0 109.2 143.5"
      width={size}
      height={size}
    >
      <path
        fill="#FAC610"
        d="M68.5,143.5H40.8c0-3.5,0-139.6,0-143.5h27.7C68.5,3.1,68.5,139.7,68.5,143.5z M27.7,71.7H0c0,4.7,0,68.7,0,71.8h27.7C27.7,139.5,27.7,76.1,27.7,71.7z M109.2,0H81.5c0,3.2,0,69.5,0,71.8h27.7C109.2,67.9,109.2,2.9,109.2,0z"
      />
    </svg>
  );
}

export function ZipLogo({ size = 18 }: LogoProps) {
  // Official Zip wordmark — purple block above white "zip" lettering. White
  // parts only read on dark backgrounds. Wide aspect (~2.7:1).
  return (
    <svg role="img" aria-label="Zip" viewBox="0 0 226.8 83" width={size} height={size}>
      <path fill="#AA8FFF" d="M77.4,25.3L84.4,83h69.4l-7.1-57.7H77.4z" />
      <path
        fill="#FFFFFA"
        d="M97.1,3.3c4.4,4.2,5,10.7,1.4,14.6c-3.7,3.9-10.2,3.7-14.7-0.5c-4.4-4.2-5-10.7-1.4-14.6C86.2-1.1,92.7-0.9,97.1,3.3z"
      />
      <path
        fill="#FFFFFA"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M226.5,46.1c-1.6-13-11.8-20.8-25.6-20.8h-46.1l7.1,57.7h20.8l-1.4-11.5h22C220.4,71.5,228.3,60.7,226.5,46.1z M200.9,55.3l-21.7,0l-1.7-13.8l21.8,0c5.1,0.1,7.8,2.9,8.2,6.9C207.8,50.9,206.6,55.3,200.9,55.3z"
      />
      <path
        fill="#FFFFFA"
        d="M5.1,66.9l2,16.1h69.3l-2.3-18.5H41.8l-0.3-2.3l29.8-20.8l-2-16.2H0l2.3,18.5h32.4l0.3,2.3L5.1,66.9z"
      />
    </svg>
  );
}

export function SezzleLogo({ size = 18 }: LogoProps) {
  // Official Sezzle four-quadrant gradient mark.
  return (
    <svg
      role="img"
      aria-label="Sezzle"
      viewBox="0 0 161.3 199.5"
      width={size}
      height={size}
    >
      <defs>
        <linearGradient
          id="sezzle-purple"
          x1="76.9"
          y1="1854.1"
          x2="44.4"
          y2="1815.5"
          gradientTransform="translate(0 -1718)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#CE5DCB" />
          <stop offset="0.2" stopColor="#C558CC" />
          <stop offset="0.6" stopColor="#AC4ACF" />
          <stop offset="1" stopColor="#8534D4" />
        </linearGradient>
        <linearGradient
          id="sezzle-pink"
          x1="78"
          y1="1810.1"
          x2="147.7"
          y2="1810.1"
          gradientTransform="translate(0 -1718)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#FF5667" />
          <stop offset="0.7" stopColor="#FC8B82" />
          <stop offset="1" stopColor="#FBA28E" />
        </linearGradient>
        <linearGradient
          id="sezzle-green"
          x1="0"
          y1="1785.1"
          x2="95.9"
          y2="1785.1"
          gradientTransform="translate(0 -1718)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#00B874" />
          <stop offset="0.5" stopColor="#29D3A2" />
          <stop offset="0.7" stopColor="#53DFB6" />
          <stop offset="1" stopColor="#9FF4D9" />
        </linearGradient>
        <linearGradient
          id="sezzle-orange"
          x1="65.4"
          y1="1850.4"
          x2="161.3"
          y2="1850.4"
          gradientTransform="translate(0 -1718)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#FCD77E" />
          <stop offset="0.5" stopColor="#FEA500" />
          <stop offset="1" stopColor="#FF5B00" />
        </linearGradient>
      </defs>
      <path
        fill="url(#sezzle-purple)"
        d="M13.5,134.2c18,18,47.3,18.1,65.3,0,0,0,0,0,0,0l1.7-1.7c9-9-9-56.3,0-65.3L13.5,134.2Z"
      />
      <path
        fill="url(#sezzle-pink)"
        d="M82.4,65.4l-1.7,1.7c-9,9,9,56.3,0,65.3l67.1-67.1c-18-18-47.3-18-65.3,0"
      />
      <path
        fill="url(#sezzle-green)"
        d="M13.5,68.8c-18,18-18.1,47.3,0,65.3,0,0,0,0,0,0l68.8-68.8c18-18,18-47.3,0-65.3L13.5,68.8Z"
      />
      <path
        fill="url(#sezzle-orange)"
        d="M78.9,134.2c-18,18-18,47.3,0,65.3l68.8-68.8c18-18,18.1-47.3,0-65.3,0,0,0,0,0,0l-68.8,68.8Z"
      />
    </svg>
  );
}

export function AfterpayLogo({ size = 18 }: LogoProps) {
  // Official Afterpay loop mark in #B2FCE4 (mint).
  return (
    <svg
      role="img"
      aria-label="Afterpay"
      viewBox="0 0 662.8 662.8"
      width={size}
      height={size}
    >
      <path
        fill="#B2FCE4"
        d="M438.3,265.5l-34.6-19.8l-35.1-20.1c-23.2-13.3-52.2,3.4-52.2,30.2v4.5c0,2.5,1.3,4.8,3.5,6l16.3,9.3c4.5,2.6,10.1-0.7,10.1-5.9v-10.7c0-5.3,5.7-8.6,10.3-6l32,18.4l31.9,18.3c4.6,2.6,4.6,9.3,0,11.9l-31.9,18.3l-32,18.4c-4.6,2.6-10.3-0.7-10.3-6l0-5.3c0-26.8-29-43.6-52.2-30.2L259.1,317l-34.6,19.8c-23.3,13.4-23.3,47.1,0,60.5l34.6,19.8l35.1,20.1c23.2,13.3,52.2-3.4,52.2-30.2v-4.5c0-2.5-1.3-4.8-3.5-6l-16.3-9.3c-4.5-2.6-10.1,0.7-10.1,5.9v10.7c0,5.3-5.7,8.6-10.3,6l-32-18.4l-31.9-18.3c-4.6-2.6-4.6-9.3,0-11.9l31.9-18.3l32-18.4c4.6-2.6,10.3,0.7,10.3,6v5.3c0,26.8,29,43.6,52.2,30.2l35.1-20.1l34.6-19.8C461.6,312.6,461.6,278.9,438.3,265.5z"
      />
    </svg>
  );
}

export function KlarnaLogo({ size = 18 }: LogoProps) {
  // Official Klarna "K" mark in #FFA8CD (Klarna pink).
  return (
    <svg role="img" aria-label="Klarna" viewBox="0 0 19.1 21.6" width={size} height={size}>
      <path
        fill="#FFA8CD"
        d="M17.1,0h-4.7c0,3.9-2.4,7.3-6,9.8l-1.4,1V0H0v21.6h4.9v-10.7l8.2,10.7h6l-7.8-10.2c3.6-2.6,5.9-6.5,5.9-11.4Z"
      />
    </svg>
  );
}

export function AffirmLogo({ size = 18 }: LogoProps) {
  // Official Affirm mark — purple arc with white "affirm" wordmark below.
  // Wide aspect (~1.36:1); white only reads on darker backgrounds.
  return (
    <svg
      role="img"
      aria-label="Affirm"
      viewBox="0 0 131.1 96.1"
      width={size}
      height={size}
    >
      <path
        fill="#4A4AF4"
        d="M21.4,94.4c-16-24.4-9.1-57,15.2-73,24.4-16,57-9.1,73,15.2,11.5,17.5,11.5,40.2,0,57.8h14.8c15.9-32.5,2.5-71.8-30-87.7C61.9-9.3,22.6,4.1,6.7,36.6c-8.9,18.2-8.9,39.5,0,57.7h14.7Z"
      />
      <path
        fill="#FFFFFF"
        d="M66.4,34.4c-9.6,0-20.6,4.5-26.6,9.3l5.5,11.5c4.8-4.4,12.6-8.2,19.6-8.2s10.3,2.2,10.3,6.7-2.4,4.7-7,5.1c-17.3,1.6-30.7,7-30.7,20.3s7.6,16.9,20.1,16.9c7.6,0,14.7-4.1,18.5-10.8v9h15.6v-37.9c0-15.7-10.9-22-25.1-22h0ZM61.9,83.9c-5,0-7.8-2.2-7.8-5.7,0-7.4,9-9.2,20.8-9.2,0,7.7-5.2,14.9-13,14.9h0Z"
      />
    </svg>
  );
}

export function JCPenneyLogo({ size = 18 }: LogoProps) {
  // Official JCPenney "JCP" wordmark in #E71324.
  return (
    <svg
      role="img"
      aria-label="JCPenney"
      viewBox="0 0 122 46.4"
      width={size}
      height={size}
    >
      <path
        fill="#E71324"
        fillRule="evenodd"
        d="M0,38.6c2.1,4.8,6.9,7.8,12.6,7.8s10-2.6,12.3-6.8c1-1.9,1.4-4.2,1.4-8V.6h-8.5v31.9c0,2.5-.4,3.6-1.1,4.4-.9,1.2-2.4,1.9-4.1,1.9-3.3,0-4.9-2.6-5.4-3.9l-7.2,3.7ZM73,33.1c-2,2.4-6,5.8-12.4,5.8s-8.2-1.6-11.1-4.5c-2.9-2.9-4.5-6.9-4.5-11.2,0-8.8,6.7-15.7,15.2-15.7s7.6,1.5,10.5,4.2l5.4-5.8c-4.2-3.8-9.8-5.9-15.9-5.9s-12.8,2.4-17.2,6.7c-4.3,4.3-6.6,10.1-6.6,16.5s2.4,12.2,6.7,16.5c4.4,4.3,10.5,6.7,17.2,6.7s14.2-2.9,18.5-8.1c0,0-5.8-5.2-5.8-5.2ZM105,25.2h-7.7V8.4h7.7c4.8,0,8.4,3.6,8.4,8.4s-3.6,8.4-8.4,8.4M117.3,5.3c-3-3-7.2-4.7-11.7-4.7h-16.7v45.1h8.5v-12.7h8.2c4.5,0,8.7-1.7,11.7-4.7s4.7-7.1,4.7-11.5c0-4.4-1.7-8.4-4.7-11.5"
      />
    </svg>
  );
}

export function MacysLogo({ size = 18 }: LogoProps) {
  // Official Macy's red star mark in #E11A2B.
  return (
    <svg
      role="img"
      aria-label="Macy's"
      viewBox="0 0 48.7 46.7"
      width={size}
      height={size}
    >
      <path
        fill="#E11A2B"
        d="M24.4,0l-5.7,17.8H0l15.1,11L9.4,46.6l15-11l15.1,11.1l-5.8-17.9l15-11H30.1L24.4,0z"
      />
    </svg>
  );
}

export function TDBankLogo({ size = 18 }: LogoProps) {
  // Official TD Bank "TD" wordmark in #19B14B.
  return (
    <svg role="img" aria-label="TD Bank" viewBox="0 0 57 51" width={size} height={size}>
      <path
        fill="#19B14B"
        d="M40,44.1h-16.8V13.5h8.5v25.1h8.2c5.6,0,8-4,8-14s-2.7-13.1-8.4-13.1h-18.7v32.6h-8.4V11.6H0v-5.4h42.1c10.2,0,14.9,5.2,14.9,18.4s-7.1,19.5-17,19.5"
      />
    </svg>
  );
}

export function NelnetLogo({ size = 18 }: LogoProps) {
  // Official Nelnet "n" mark — green gradient ring with stylized "n" inside.
  // Gradient id is per-instance (same reasoning as VerizonLogo above): SVG ids
  // are document-global, so two Nelnet marks on one page both pointed at the
  // first <defs>, and the survivors lost their fill the moment that instance
  // unmounted — the logo silently rendered as nothing.
  const gradId = `nelnet-grad-${useId().replace(/:/g, '')}`;
  return (
    <svg role="img" aria-label="Nelnet" viewBox="0 0 160 160" width={size} height={size}>
      <defs>
        <linearGradient
          id={gradId}
          x1="42.1"
          y1="35.8"
          x2="117.5"
          y2="142"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#AFD135" />
          <stop offset="1" stopColor="#70BA44" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradId})`}
        d="M84.9,40.5c-8.6,0-14.7,3.9-19.8,8.4l-.3-5.3c0-.7-.6-1.2-1.2-1.2h-16.4c-.7,0-1.2.6-1.2,1.2,0,0,0,0,0,0v74.5c0,.7.5,1.2,1.2,1.2h18.3c.7,0,1.2-.6,1.2-1.2v-51.4c6.7-7.7,9.6-8.9,14-8.9s12.7,4.2,12.7,16.1v44.2c0,.7.5,1.2,1.2,1.2,0,0,0,0,0,0h18.3c.7,0,1.2-.6,1.2-1.2v-48c0-19.5-14.7-29.7-29.2-29.7"
      />
      <path
        fill={`url(#${gradId})`}
        d="M80,0C35.8,0,0,35.8,0,80h0c0,44.2,35.8,79.9,80,80h0c44.2,0,79.9-35.8,80-80h0C160,35.8,124.2,0,80,0ZM16.5,80c0-35.1,28.5-63.5,63.5-63.5h0c35.1,0,63.5,28.5,63.5,63.5h0c0,35.1-28.5,63.5-63.5,63.5h0c-35.1,0-63.5-28.5-63.5-63.5"
      />
    </svg>
  );
}

export function MOHELALogo({ size = 18 }: LogoProps) {
  // Official MOHELA mark — student loan servicer. Source viewBox is offset.
  return (
    <svg
      role="img"
      aria-label="MOHELA"
      viewBox="-287 122.4 108 156.6"
      width={size}
      height={size}
    >
      <path
        fill="#B91931"
        d="M-224.2,185.2c-0.8,1.1,2.9,0.5,10.9-2c12.1-3.7,17.8,0.4,22.9,4.8c5,4.4,6.2,7.1,8,13.5c1.8,6.4,2.9,15.4,1.3,24.3s-3.2,9.6-7.4,15c-3.5,4.5-12.3,9.9-14.5,13.3c-7.4,11.1-3.3,13-6.3,17.4c-2,2.9-4.6,5-8,6.2c-3.1,0.4-6.2,0.4-9.2,0c-3.1-0.4-6.2-1.3-9.1-2.6c-6.7,1.7-11.7,2.6-15.2,2.6c-5.2,0-11.4-1.9-15.8-4.1c-4.4-2.2-13.1-14.9-16.2-22.1c-3.1-7.1-4.2-18.8-4.2-29.4c0-10.6,0-16.3,2.1-24.7c2.1-8.5,5.5-16.4,10.6-21.2s14.3-9,22.5-9s11.5,1.1,16.2,5C-232.5,174.9-228.6,179.2-224.2,185.2z M-229.8,189.9c2.1-1.6,2.7-2.3,2.1-3.7c-2.1-4.8-3.9-6.5-6.9-9c-6.3-5.4-14.9-5.7-24.1-2.9c-9.2,2.8-14.8,12-17.4,18.2c-2.6,6.2-5.4,10.8-5.7,29.5c-0.3,18.6,4.3,26.5,5.7,30.4c1.4,3.9,8.9,14.6,13.6,16.3c4.7,1.8,6.5,2.9,12.4,2.5c5.9-0.4,7.3-2.5,12.7-2.5s7,0.9,8.9,1.8c2,0.8,9.3,1.2,11.6,0c2.3-1.2,2.1-4.6,3.5-9.1c1.4-4.5,4.9-10.7,12.9-18.7c2.3-2.3,10.8-7.7,12.2-10.8c5.2-11.8,2.9-25.8,0-30.6c-2.9-4.8-4.5-8.4-7.1-10.2c-2.6-1.8-7.3-4.5-14.7-2.8c-7.4,1.7-11.8,5.6-13.6,5.8C-225,194.2-227,192.8-229.8,189.9z"
      />
      <path
        fill="#B01734"
        d="M-266.2,192.4c2.8-6.5,5.2-10.4,7-11.9c3-2.3,5.3-3.3,9-4.2c2.3-0.5,4.9-0.6,7.6-0.1l0,0c0.3,0,0.4,0.3,0.4,0.6c0,0,0,0.1,0,0.1c-1.8,4.5-2.9,6.9-3.3,7.1c-0.6,0.4-5.6-1.5-10.8,1.5c-3.1,1.8-6.3,4.3-9.6,7.5L-266.2,192.4C-266.2,192.4-266.2,192.4-266.2,192.4z"
      />
      <path
        fill="#568071"
        d="M-232.9,131.8c-3,3.8-4,11.2-3,18.2c0.7,4.6,6,7.2,15.9,7.6v4.6c-9.8-0.2-15.9-1.7-18.3-4.6c-3.6-4.3-3.4-7.9-3.4-13.2c0-3.6,1.9-11.2,5.8-23c11,6.7,17,10.8,18,12.3c2.6,3.7,4.4,7.6,5.3,11.8c0.2,3.8-0.1,6.2-0.8,7.4c-1,1.5-2.3,2.2-3.8,2.2c-0.2-5.3-1.2-9.7-2.8-13.3C-222.4,136.4-231.2,129.6-232.9,131.8z"
      />
      <path
        fill="#568071"
        d="M-211.7,166.6c-2,1.1-2.7,3.2-2.2,6.3c0.7,4.6,8.6,10.1,22.2,5.3c9.1-3.2,13.3-6.1,12.7-8.8c-1.5-4-3.6-6.6-6.4-8c-4.1-2-7.8-3-12-2.7c-2.8,0.2-5.2,1.8-7.1,4.7c2.7-0.6,4.7-0.9,5.9-0.8c6.8,0.5,10.9,3,12.4,7.4c-3.7,2.3-6.7,3.9-9,4.6c-1.7,0.5-9.7,1.4-13-1C-210.5,172-211.7,169.7-211.7,166.6z"
      />
      <path
        fill="#568071"
        d="M-218.3,162.4c6.5-8.6,16.5-13.4,19.4-15c2.8-1.6,3.7-2.8,6-1.4c2.3,1.3,4.5,3.2-2,7.5c-6.5,4.3-9.8,5.3-14.2,8.9c-2.9,2.4-7.7,9.5-14.5,21.2l-4.3-5.8C-225.9,173.2-222.7,168.1-218.3,162.4z"
      />
    </svg>
  );
}

export function KelleyBlueBookLogo({ size = 18 }: LogoProps) {
  // Official Kelley Blue Book ribbon mark — navy and gold shield with the
  // company name. Tall aspect (~0.71:1) so it will appear narrow in the
  // icon column but stays readable at the chosen size.
  return (
    <svg
      role="img"
      aria-label="Kelley Blue Book"
      viewBox="0 0 57.11 80"
      width={size}
      height={size}
    >
      <path
        fill="#1e3f74"
        d="m47.45 80a1.64 1.64 0 1 1 1.63-1.64 1.63 1.63 0 0 1 -1.63 1.64zm0-3a1.4 1.4 0 1 0 1.38 1.4 1.39 1.39 0 0 0 -1.38-1.4zm.4 2.3-.42-.73h-.27v.73h-.25v-1.8h.61a.55.55 0 0 1 .57.54.49.49 0 0 1 -.39.49l.43.77zm-.35-1.58h-.34v.63h.34a.31.31 0 0 0 .34-.31.32.32 0 0 0 -.34-.36z"
      />
      <path
        fill="#1e3f74"
        d="m17.63 9.73 3.71 2.59 1.65-4.22 3.14 3.27 2.43-3.81 2.44 3.81 3.13-3.26 1.65 4.21 3.71-2.59.79 4.46 2.77-1.21v-11.59h-29v11.57l2.79 1.22z"
      />
      <path
        fill="#1e3f74"
        d="m39.48 62.5-3.71-2.59-1.65 4.22-3.13-3.27-2.44 3.81-2.44-3.81-3.13 3.26-1.65-4.21-3.71 2.58-.79-4.45-2.79 1.22v17.9l14.52-11.14 14.51 11.14v-17.89l-2.8-1.22z"
      />
      <path
        fill="#f1bb25"
        d="m53.3 38.56 3.81-2.44-3.81-2.44 3.27-3.13-4.22-1.65 2.59-3.71-4.45-.79 1.81-4.15-4.52.09 1-4.41-4.32.94v-16.87h-31.8v16.86l-4.29-.94 1 4.41-4.52-.09 1.81 4.15-4.45.79 2.58 3.71-4.24 1.65 3.26 3.13-3.81 2.44 3.81 2.44-3.26 3.13 4.21 1.65-2.59 3.67 4.45.8-1.81 4.2 4.52-.08-1 4.41 4.3-.94v24.61l15.93-12.23 15.9 12.23v-24.63l4.28.94-1-4.42 4.56.11-1.82-4.14 4.46-.79-2.59-3.71 4.21-1.65zm-39.25-37.17h28.95v11.61l-2.76 1.21-.79-4.46-3.71 2.59-1.61-4.23-3.13 3.26-2.44-3.81-2.43 3.81-3.13-3.27-1.65 4.22-3.72-2.59-.79 4.45-2.79-1.18zm29 75.77-14.49-11.16-14.56 11.16v-17.9l2.83-1.26.79 4.45 3.71-2.58 1.67 4.25 3.13-3.26 2.44 3.81 2.43-3.81 3.13 3.27 1.65-4.22 3.71 2.59.79-4.45 2.8 1.22v17.89z"
      />
      <path
        fill="#1e3f74"
        d="m28.54 12.39a23.74 23.74 0 1 0 23.77 23.76 23.75 23.75 0 0 0 -23.77-23.76z"
      />
      <g fill="#fff">
        <path d="m42.87 26.9-2.3 1.6-.75-1.08 2.29-1.6 2.12-4 .83 1.18-1.52 2.61 3-.55.83 1.18z" />
        <path d="m35.56 23.55 4.13-5.41 3.57 2.71-.71.94-2.55-1.91-1 1.27 2.14 1.63-.72.94-2.14-1.63-1 1.32 2.51 1.91-.72.94z" />
        <path d="m30.77 21.93 1.83-6.56 1.28.36-1.51 5.41 3 .82-.37 1.15z" />
        <path d="m25.62 22.08-.62-6.77 1.33-.12.5 5.59 3.08-.28.09 1.18z" />
        <path d="m20.64 24.08-2.84-6.18 4.08-1.9.5 1.08-2.88 1.34.67 1.45 2.45-1.11.49 1.07-2.45 1.17.7 1.5 2.87-1.31.5 1.07z" />
        <path d="m19.08 25.54-3.49-.74.21 1.36 1.54 1.35-.89 1-5.11-4.51.89-1 2.22 2-.63-3.76 1.07-1.24.54 3.47 4.68.91z" />
        <path d="m48.51 39.9-1.62-3.42-.81 1.16v2.26h-1.23v-7.51h1.23v3.27l2.22-3.27h1.49l-2.09 3 2.24 4.52z" />
        <path d="m43.42 39.14a2.18 2.18 0 0 1 -3.41 0c-.64-.76-.62-1.7-.62-3s0-2.23.62-3a2.18 2.18 0 0 1 3.41 0c.63.76.62 1.7.62 3s.01 2.24-.62 3zm-.92-5.14a.93.93 0 0 0 -.78-.41 1 1 0 0 0 -.8.41c-.24.31-.3.66-.3 2.11s.06 1.79.3 2.1a1 1 0 0 0 .8.41.93.93 0 0 0 .78-.41c.24-.31.31-.66.31-2.1s-.07-1.76-.31-2.11z" />
        <path d="m38.17 39.14a2.17 2.17 0 0 1 -3.4 0c-.64-.76-.62-1.7-.62-3s0-2.23.62-3a2.17 2.17 0 0 1 3.4 0c.64.76.63 1.7.63 3s.01 2.24-.63 3zm-.91-5.14a1 1 0 0 0 -1.58 0c-.24.31-.3.66-.3 2.11s.06 1.79.3 2.1a1 1 0 0 0 1.58 0c.24-.31.31-.66.31-2.1s-.07-1.76-.31-2.11z" />
        <path d="m31.63 39.9h-2.63v-7.51h2.53a1.88 1.88 0 0 1 1.95 2.11 1.79 1.79 0 0 1 -.78 1.55 1.79 1.79 0 0 1 .87 1.69 1.91 1.91 0 0 1 -1.94 2.16zm-.21-6.2h-1.2v1.73h1.2a.87.87 0 0 0 0-1.73zm.08 3h-1.28v1.84h1.28a.83.83 0 0 0 .83-.93.81.81 0 0 0 -.83-.86z" />
        <path d="m22.49 39.9v-7.51h4.15v1.31h-2.92v1.76h2.49v1.31h-2.49v1.82h2.92v1.31z" />
        <path d="m19.26 40a2.4 2.4 0 0 1 -2.32-2.63v-4.98h1.23v4.88c0 .87.42 1.39 1.09 1.39s1.09-.52 1.09-1.39v-4.88h1.22v4.94a2.39 2.39 0 0 1 -2.31 2.67z" />
        <path d="m12.51 39.9v-7.51h1.23v6.2h2.86v1.31z" />
        <path d="m9.78 39.9h-2.64v-7.51h2.53a1.88 1.88 0 0 1 1.95 2.11 1.79 1.79 0 0 1 -.78 1.55 1.79 1.79 0 0 1 .86 1.69 1.9 1.9 0 0 1 -1.92 2.16zm-.21-6.2h-1.2v1.73h1.2a.87.87 0 0 0 0-1.73zm.08 3h-1.28v1.84h1.28a.83.83 0 0 0 .83-.93.81.81 0 0 0 -.83-.86z" />
        <path d="m12.7 47.06a1.42 1.42 0 0 1 -.95-.62 1.46 1.46 0 0 1 -.24-1.13c.11-.56.53-.84 1.09-1.24s1-.69 1.52-.6a1.5 1.5 0 0 1 1.18 1.75c-.11.56-.52.85-1.08 1.24s-.97.69-1.52.6zm1.9-2a.65.65 0 0 0 -.1-.53.67.67 0 0 0 -.45-.29c-.22 0-.4 0-1 .49s-.76.58-.81.8a.61.61 0 0 0 .1.53.62.62 0 0 0 .45.29c.22 0 .39 0 1-.49s.76-.61.81-.86z" />
        <path d="m16.09 46.78-.71.69 1 1.12-.5.48-1-1.12-1.16 1.14-.52-.56 2.86-2.79 1.76 1.87-.5.49z" />
        <path d="m18.44 49-.57.8 1.22.92-.4.56-1.23-.91-.95 1.32-.61-.45 2.34-3.24 2.05 1.52-.41.57z" />
        <path d="m19 53.32 1.82-3.59.67.35-1.82 3.59z" />
        <path d="m21.77 54.6a1.39 1.39 0 0 1 -.83-.78 2.12 2.12 0 0 1 .18-1.64c.24-.66.4-1.14.9-1.39a1.38 1.38 0 0 1 1.13 0 1.4 1.4 0 0 1 .9 1.7l-.72-.27a.67.67 0 0 0 -.41-.77.64.64 0 0 0 -.53 0c-.19.11-.3.28-.56 1s-.28.93-.2 1.14a.57.57 0 0 0 .38.36.63.63 0 0 0 .8-.31l.72.27a1.36 1.36 0 0 1 -1.76.69z" />
        <path d="m24 55.29.81-4 .74.16-.81 4z" />
        <path d="m27.79 55.82-.17-.74-1.36-.08-.29.7-.78-.07 1.71-3.91.59.05 1.08 4.14zm-.67-2.92-.6 1.43.95.08z" />
        <path d="m29.07 55.82-.23-4h.75l.19 3.33 1.75-.15v.71z" />
        <path d="m35.43 54.05a1.42 1.42 0 0 1 -.87.81 1.36 1.36 0 0 1 -1.12 0c-.51-.25-.66-.73-.9-1.39a2.15 2.15 0 0 1 -.18-1.65 1.45 1.45 0 0 1 2.64-.11l-.72.26a.64.64 0 0 0 -.85-.31.63.63 0 0 0 -.43.34c-.08.21 0 .4.21 1.13s.36.9.55 1a.64.64 0 0 0 .53 0 .67.67 0 0 0 .42-.4.8.8 0 0 0 0-.57l-.05-.14-.67.24-.22-.62 1.37-.5.19.55a1.64 1.64 0 0 1 .1 1.36z" />
        <path d="m38.1 53.35a1.35 1.35 0 0 1 -1.92-.53l-1.27-2.33.66-.37 1.26 2.3a.64.64 0 0 0 .93.32.68.68 0 0 0 .24-1l-1.26-2.29.65-.37 1.27 2.32a1.39 1.39 0 0 1 -.56 1.95z" />
        <path d="m40.34 51.9-2.5-3.13.59-.49 2.5 3.14z" />
        <path d="m43.15 48.83a1.42 1.42 0 0 1 -.46 1l-1 1-2.78-2.83 1-1a1.36 1.36 0 0 1 1-.45c.62 0 1 .44 1.4.87a2.21 2.21 0 0 1 .84 1.41zm-2.24-1.54a.64.64 0 0 0 -.57.22l-.41.41 1.82 1.88.4-.41a.68.68 0 0 0 .23-.58c0-.23-.21-.46-.63-.89s-.62-.6-.84-.63z" />
        <path d="m44.23 48-3.3-2.24 1.41-2.16.57.39-1 1.52.78.52.84-1.29.57.39-.84 1.29.8.55 1-1.52.58.39z" />
      </g>
      <path
        fill="#f1bb25"
        d="m28.49 24.69a11.39 11.39 0 0 1 10.45 6.87h.7a12 12 0 0 0 -22.3 0h.66a11.39 11.39 0 0 1 10.49-6.87z"
      />
      <path
        fill="#f1bb25"
        d="m28.49 47.5a11.41 11.41 0 0 1 -10.42-6.78h-.7a12 12 0 0 0 22.24 0h-.71a11.39 11.39 0 0 1 -10.41 6.78z"
      />
    </svg>
  );
}

export function EmpowerFCULogo({ size = 18 }: LogoProps) {
  // Official Empower FCU mark — interlocking gold (#B18C2E) and blue (#0C549C)
  // swooshes with the white "E" wordmark centered. 24x24 viewBox.
  return (
    <svg
      role="img"
      aria-label="Empower Federal Credit Union"
      viewBox="0 0 24 24"
      width={size}
      height={size}
    >
      <path
        fill="#B18C2E"
        d="M6.8,6.7c8.6-2.7,15.4-1.7,15.1,2.2,0,1.6-1.3,3.3-3.2,5.1,3.1-2.2,5.2-4.6,5.3-6.6.2-3.7-6.2-4.7-14.4-2.1-2.6.8-5.1,1.9-7.3,3.1,1.4-.6,2.9-1.2,4.4-1.7"
      />
      <path
        fill="#0C549C"
        d="M17.2,17.3c-8.6,2.7-15.4,1.7-15.1-2.2,0-1.6,1.3-3.3,3.2-5.1C2.2,12.2.2,14.6,0,16.6c-.2,3.7,6.2,4.7,14.4,2.1,2.6-.8,5.1-1.9,7.3-3.1-1.4.7-2.9,1.3-4.4,1.7"
      />
      <polygon
        fill="#fff"
        points="11.2 9.2 16.4 9.2 16.8 8 9.8 8 7.2 16 15 16 15.4 14.8 9.4 14.8 9.4 14.8 10.1 12.4 15.4 12.4 15.8 11.1 10.5 11.1 11.2 9.2 11.2 9.2"
      />
    </svg>
  );
}

export function OptumLogo({ size = 18 }: LogoProps) {
  // Official Optum mark — orange (#FF612B) ring forming an "O".
  return (
    <svg
      role="img"
      aria-label="Optum Bank"
      viewBox="0 0 164 160.4"
      width={size}
      height={size}
    >
      <path
        fill="#FF612B"
        d="M122.8,80.2c0,24.6-18.1,43-40.8,43s-40.8-18.4-40.8-43s18.1-43,40.8-43S122.8,55.6,122.8,80.2z M82,0C36.1,0,0,35.6,0,80.2s36.1,80.2,82,80.2s82-35.6,82-80.2S127.9,0,82,0z"
      />
    </svg>
  );
}

export function WheatlandBankLogo({ size = 18 }: LogoProps) {
  // Official Wheatland Bank wordmark in #DD001B. Wide aspect (~2.2:1) so it
  // appears smaller in the icon column than the circular marks.
  return (
    <svg
      role="img"
      aria-label="Wheatland Bank"
      viewBox="0 0 139.7 63.1"
      width={size}
      height={size}
    >
      <g fill="#DD001B">
        <path d="M43.2,58.5l-5.4-21.1c-.3-1.4-.7-3.1-.7-3.7,0-1.2.7-1.5,3-1.9,1.8-.3,2.7-.5,3.1-.5,1.1,0,1.4.6,1.8,2.5,1.8,7.9,3.1,14,4.3,21.1,0,.2.1.2.3.2s.3,0,.3-.2c1.4-6.6,2.8-13.7,4.3-19.6.9-3.7.9-3.7,5.4-3.7s4.5,0,5.3,3.7c1.8,7.7,3,12.8,4.4,19.6,0,.2.1.2.3.2s.2,0,.3-.2c1.2-6.9,2.7-14.1,4.2-21,.4-1.7.6-2.7,1.8-2.7s1.7.2,3.2.5c2.5.5,2.9.9,2.9,1.8s-.3,2.1-.6,3.7l-5.4,21.2c-.7,2.6-2.8,3.9-6.3,3.9s-5.6-1.3-6-3.9l-3.8-17c0-.2-.1-.2-.3-.2s-.2,0-.3.2l-3.9,17c-.5,2.6-2.5,3.9-6,3.9-5.3,0-5.3,0-6.3-3.9h0Z" />
        <path d="M88.4,59.9c-1-.7-1.4-1.4-1.4-2.8V24.9c0-2.5,1.3-3.8,3.9-3.8s3.9,1.3,3.9,3.8v7.5c1.7-.8,4.3-1.4,6.7-1.4,7,0,12,4.8,12.6,11.7.2,2.3.2,6.2,0,8.5-.6,6.9-5.6,11.7-13.5,11.7s-10.4-1.9-12.3-3.1h0ZM100.3,55.5c3.8,0,5.8-2.2,6-5.5.1-1.7.2-3.8,0-5.8-.3-3.3-2.2-5.5-6-5.5s-4.3.5-5.5,1.4v14.1c1.2.9,3.5,1.4,5.5,1.4h0Z" />
        <path d="M17,42.8C17,22.9,53.4,6.7,98.3,6.7s26.8,2.3,39.8,6.2c1.5.5,2.3-1.7.9-2.3C123.6,4,108.5,0,85.4,0,38.2,0,0,17,0,37.9s7.9,18.2,20.9,24.9c3.2,1.6,6-2.5,3.5-5-4.7-4.6-7.4-9.7-7.4-15" />
      </g>
    </svg>
  );
}

export function TRowePriceLogo({ size = 18 }: LogoProps) {
  // T. Rowe Price tile mark — cyan bighorn-ram silhouette (#00CFEC) on a
  // white square. Designed to read as a full-bleed badge, so the white
  // rect is part of the artwork rather than a transparent backdrop.
  return (
    <svg
      role="img"
      aria-label="T. Rowe Price"
      viewBox="0 0 1552 1553"
      width={size}
      height={size}
    >
      <path fill="#FFFFFF" d="M0 0h1552v1553H0z" />
      <path
        fill="#00CFEC"
        fillRule="evenodd"
        d="m0 0h1552v1552.5l-2.2-3.5c-1.2-1.9-7.8-12-14.6-22.5-6.9-10.5-17.2-25.8-23-34-5.8-8.3-16.2-22.7-23.2-32-7-9.3-18.8-24.4-26.2-33.5-7.4-9.1-17.5-21-22.3-26.5-4.8-5.5-15.9-17.7-24.6-27-8.7-9.3-24.5-25.2-35.1-35.2-10.6-10-26-23.8-34.3-30.8-8.3-7-21.8-17.8-30-23.9-8.3-6.2-20.2-14.9-26.5-19.2-6.3-4.4-16.2-10.9-22-14.6-5.8-3.6-17.3-10.4-25.5-15.1-8.3-4.7-20-11-26-14-8-4-11.6-5.3-13.4-4.8-1.3 0.3-2.9 1.2-3.5 2-0.7 0.8-0.9 3.1-0.5 6 0.4 2.5 1.4 8.6 2.3 13.6 0.8 5 2 13.7 2.6 19.5 0.5 5.8 1 20.4 1 32.5 0 12.1-0.5 27.2-1 33.5-0.6 6.3-1.7 17.1-2.6 24-0.8 6.9-2.7 18.6-4 26-1.4 7.4-4 19.7-5.9 27.3-1.9 7.5-5.1 18.5-7 24.4-1.9 6-4.3 11.8-5.5 13.1-1.2 1.3-3.2 2.2-5 2.2-2 0-3.5-0.7-4.4-2.2-0.8-1.3-2.4-9.8-3.6-19.3-1.1-9.3-3.2-24.2-4.6-33-1.3-8.8-4.2-25.7-6.5-37.5-2.2-11.8-6-30.7-8.5-42-2.5-11.3-7-30-9.9-41.5-2.9-11.5-7.2-27.5-9.5-35.5-2.2-8-6.4-21.7-9.2-30.5-2.7-8.8-7.7-23.2-10.9-32-3.4-9.2-7-17.2-8.4-18.7-1.4-1.6-3.4-2.8-4.5-2.8-1.1 0-2.8 0.8-3.8 1.8-1 0.9-3.7 7.8-6.1 15.2-2.3 7.4-7 19.8-10.3 27.5-3.3 7.7-7.8 17.6-10 22-2.2 4.4-7 13.2-10.6 19.5-3.5 6.3-11.3 18.7-17.1 27.5-5.8 8.8-12.3 17.7-14.3 19.8-3 2.9-4.5 3.6-7 3.5-2.3-0.2-3.7-1.1-4.8-3-1.4-2.5-1.1-4.9 3.7-34 2.9-17.2 6.4-40.1 7.7-50.8 1.4-10.7 3.2-26.9 4-36 0.8-9.1 2.1-26.2 2.7-38 0.7-12.8 0.9-32.8 0.6-49.5-0.3-15.4-1.1-33.2-1.7-39.5-0.6-6.3-2-17.6-3.1-25-1.6-11-2.3-13.9-4.2-15.8-1.2-1.2-2.9-2.2-3.7-2.2-0.8 0-2.2 0.6-3.1 1.3-0.9 0.7-4.9 7.1-8.9 14.2-4 7.1-11 18.6-15.6 25.5-4.5 6.9-12 17-16.5 22.5-4.6 5.5-12.8 14.5-18.4 19.9-5.5 5.4-14.7 13.7-20.5 18.3-5.8 4.6-17.5 13.1-26 18.7-8.5 5.7-26.8 17-40.5 25.1-13.8 8.2-32.2 19.7-41 25.6-8.8 5.9-22.3 15.9-30 22.2-7.7 6.3-19.9 17.5-27.1 24.9-7.2 7.3-17.9 19.4-23.8 26.8-5.9 7.4-14.9 19.8-20 27.5-5.2 7.7-12.4 19.4-16.1 26-3.8 6.6-7.9 13.7-9.2 15.8-1.3 2.1-3.8 4.4-5.8 5.2-2.8 1.2-4.2 1.3-7.2 0.4-2.5-0.8-6.1-3.5-10.3-7.9-4.6-4.7-8-9.6-11.7-16.6-2.8-5.4-6.5-13.9-8.2-18.9-1.7-5-4.1-14.2-5.5-20.5-1.3-6.3-3-16-3.7-21.5-0.8-6-1.3-20.6-1.3-36.5 0.1-14.6 0.6-34.4 1.3-44 0.6-9.6 1.8-25.8 2.7-36 0.8-10.2 2.8-30.7 4.5-45.5 1.7-14.8 4.3-36.2 6-47.5 1.6-11.3 4.5-29.5 6.4-40.5 1.9-11 5-27.9 7-37.5 2-9.6 5.4-25 7.5-34.3 2.2-9.2 4.3-16.9 4.8-17.1 0.4-0.3 3.2 1.1 6.2 3.1 3 1.9 8.2 4.8 11.5 6.3 3.3 1.5 8.9 3.8 12.5 4.9 3.6 1.2 10.8 2.9 16 3.7 6.1 1 15.8 1.5 27 1.5 12.4-0.1 21.5-0.7 31-2.1 7.4-1.1 19.1-2.8 26-3.6 8.4-1.1 16.3-1.4 24-1 6.3 0.3 16.7 1.7 23 3 6.3 1.3 19.6 5.2 29.5 8.6 9.9 3.4 26.5 9 37 12.5 10.5 3.5 24.6 7.6 31.5 9 6.9 1.4 19 3 27 3.6 8 0.5 21.9 0.7 31 0.3 9.1-0.3 20.2-1 24.8-1.5 5.3-0.6 9.3-1.6 11.2-2.9 2-1.3 4.5-5 7.4-11.3 2.5-5 6.3-14.6 8.5-21.2 2.2-6.6 5.1-16.5 6.4-22 1.4-5.5 2.8-12 3.2-14.5 0.4-3 0.2-5.5-0.7-7.3-0.7-1.7-3-3.6-5.3-4.7-2.2-1-7.5-2.4-11.7-2.9-4.3-0.6-19.4-1.1-33.5-1.1-14.8 0-33.1 0.7-42.8 1.5-9.4 0.8-19.7 2-23 2.5-3.3 0.5-7.5 1-9.3 1-1.7 0-3.9-0.7-4.7-1.5-0.8-0.8-1.9-3.2-2.4-5.3-0.6-2-1.3-6.4-1.7-9.7-0.7-5.6-0.6-6.2 4.6-11.5l22.3 0.6c14.7 0.5 25.7 0.3 32.7-0.5 7.6-0.9 12-1.9 16-3.9 3.4-1.7 6.4-4 7.8-6.2 1.3-1.9 3-6 3.9-9 0.9-3 2.7-6.5 3.9-7.8 1.3-1.2 3.2-2.2 4.4-2.2 1.1 0 2.7 1 3.5 2.2 0.9 1.3 2.4 5 3.4 8.3 1 3.3 2.6 7.5 3.5 9.2 1.1 2.3 3.1 4 6.3 5.4 4.2 1.9 6.1 2.1 14.8 1.7 7.5-0.3 11-0.9 14-2.4 2.2-1.2 4.8-3.4 5.7-5 1-1.6 2.6-7.4 3.7-12.9 1.1-5.5 2.4-15.2 3-21.5 0.6-6.3 1.1-19.8 1.1-30 0-12.3-0.5-21.5-1.5-27.5-0.9-5-3.2-12.8-5.1-17.5-1.9-4.7-5.6-12.2-8.2-16.7-2.6-4.6-11.3-17.8-19.4-29.5-8.7-12.6-17.2-26.2-20.8-33.3-3.3-6.6-6.7-14.7-7.5-18-0.8-3.3-1.5-8.7-1.5-12 0-3.3 0.6-8.3 1.4-11 0.8-2.8 3.2-7.3 5.3-10 2.7-3.5 5.8-6 10.3-8.3 5.5-2.7 7.6-3.2 13.5-3.2 5.2 0 7.8 0.5 10.2 2 1.8 1.1 4.1 3.2 5 4.7 1.3 2 1.8 4.6 1.7 9.8-0.1 3.8-0.9 9.7-1.9 13-0.9 3.3-2.6 7.7-3.6 9.7-1 2.1-1.8 4-1.7 4.3 0.2 0.2 4.1-0.2 8.8-0.9 4.7-0.8 11.9-2.4 16-3.6 4.1-1.2 11.5-4.3 16.4-6.9 5.9-3 11.4-7 16.4-11.6 4.1-3.9 9.5-10.1 12-14 2.6-3.9 5.8-9.7 7.2-13 1.4-3.3 3-6.1 3.5-6.2 0.6-0.1 1.4 4 1.8 9 0.4 5.1 1.5 16.6 2.3 25.7 0.8 9.1 2.1 21.9 3 28.5 0.8 6.6 2.4 17.6 3.5 24.5 1.1 6.9 3.4 18.1 5 25 1.6 6.9 4.6 17.5 6.6 23.5 1.9 6 5.7 15.5 8.2 21 2.6 5.5 7.3 14 10.5 19 3.2 5 9.1 12.3 13 16.4 3.9 4 9.8 9.3 13.1 11.6 3.3 2.4 10.3 6.4 15.5 8.9 6.2 3 13.3 5.5 20.5 7.1 6 1.4 16.2 2.8 22.5 3.1 7.1 0.4 15.1 0.2 21-0.6 5.7-0.7 14.1-2.8 21-5.2 6.3-2.2 12.8-4.1 14.5-4.1 2.5-0.2 3.1 0.2 3.3 2.3 0.2 1.4-0.9 5-2.4 8-1.5 3-5 7.7-7.8 10.4-2.8 2.7-7.8 6.4-11.1 8.3-3.3 2-9.8 4.8-14.5 6.4-4.7 1.5-12.8 3.3-18 4-7 0.8-12.9 0.8-22.5 0-7.2-0.6-16.4-1.8-20.5-2.7-4.1-0.9-10.2-2.5-13.5-3.6-3.3-1.1-9.2-3.5-13-5.2-3.8-1.8-10.6-5.6-15-8.5-4.4-3-10.9-7.9-14.4-11-3.5-3.1-9.8-9.6-14-14.5-4.2-4.8-8.4-9.2-9.4-9.7-1.1-0.6-2.5-0.4-4.2 0.6-1.8 1.1-2.5 2.4-2.5 4.7 0 1.8 0.9 6.9 2.1 11.3 1.1 4.4 3.7 11.6 5.8 16 2 4.4 5.5 10.7 7.8 14 2.2 3.3 6.7 8.9 9.9 12.4 3.2 3.5 9.3 8.9 13.4 12 4.1 3.1 11.1 7.5 15.5 9.8 4.4 2.3 12.7 5.7 18.5 7.6 5.8 1.9 15.2 4.3 21 5.3 7.8 1.4 15.1 1.9 28 1.8 12 0 20.2-0.6 26-1.7 4.7-0.9 12.1-2.9 16.5-4.3 4.4-1.4 11.2-4.1 15-5.9 3.8-1.8 10.6-5.7 15-8.6 4.4-3 11.8-9.2 16.5-13.9 5.2-5.1 11.5-12.9 15.8-19.5 4-6 9.3-15.7 11.7-21.5 2.4-5.8 5.5-15 6.9-20.5 1.4-5.5 3-14 3.7-19 0.7-5.2 0.9-14.3 0.6-21.5-0.3-8.2-1.3-15.3-2.7-20.5-1.1-4.4-3.8-11.6-5.9-16-2.3-4.7-6.1-10.4-9.2-13.8-3-3.2-6.5-6.1-7.9-6.5-1.4-0.3-3.2-0.5-4-0.3-0.8 0.1-2.3 1-3.3 1.9-1 1-3.4 6.9-5.4 13.2-1.9 6.3-5.3 15.1-7.5 19.5-2.3 4.4-5.7 10.5-7.8 13.5-2 3-6.7 8.3-10.4 11.7-4.1 4-9.3 7.6-13.9 9.8-3.9 1.9-9.7 3.9-12.7 4.5-3 0.6-9.9 1-15.3 1-7.7 0-11.3-0.5-17.5-2.6-4.3-1.4-10.5-4.5-14-7-3.4-2.4-8.2-7-10.8-10.2-2.5-3.1-6.5-9.7-8.9-14.7-2.4-5-5.3-12.4-6.4-16.5-1.1-4.1-2.5-11.3-3.2-16-0.8-5.7-1-16.3-0.6-32.5 0.3-13.2 1.5-32.5 2.6-43 1.1-10.5 3.4-32.3 5-48.5 1.7-16.2 3.5-39.2 4.1-51 0.8-15.4 0.8-27.2 0-41.5-0.6-11-1.8-25.2-2.6-31.5-0.8-6.3-2.6-17.1-4-24-1.5-6.9-4-17-5.6-22.5-1.6-5.5-4.6-14.1-6.5-19-2-4.9-5.8-13.5-8.6-19-2.7-5.5-7.4-13.8-10.5-18.5-3-4.7-7.9-11.4-10.7-15-2.9-3.6-9-10.1-13.6-14.6-4.6-4.4-11.8-10.6-15.9-13.6-4.1-3.1-11.1-7.6-15.5-10-4.4-2.5-11.2-5.9-15-7.5-3.8-1.7-10.6-4.2-15-5.6-4.4-1.4-10.7-3.3-14-4.1-3.3-0.8-11-2.3-17-3.3-8.4-1.3-16.6-1.8-34.5-1.8-17.9 0-26.2 0.5-35 1.9-6.3 1-17.4 3.3-24.5 5.1-7.1 1.7-18.6 5.5-25.5 8.4-6.9 2.8-16.5 7.4-21.5 10.2-5 2.7-12.9 7.8-17.7 11.2-4.8 3.4-12.2 9.3-16.5 13.2-4.3 3.8-11.5 11-16.1 16-4.5 4.9-12 14.2-16.5 20.5-4.5 6.4-11.1 16.7-14.7 23-3.5 6.3-8.4 15.8-15 30.5h-28.2l-9-15.5c-5-8.5-12.6-20.7-16.9-27-4.3-6.3-12.6-17.6-18.5-25-5.9-7.4-17.5-20.3-25.8-28.6-8.3-8.2-19.8-18.9-25.6-23.5-5.8-4.7-14.8-11.5-20-15.1-5.2-3.6-14.5-9.4-20.5-13-6-3.6-14.1-8.1-18-10-3.9-2-13.3-6.3-21-9.6-7.7-3.3-19-7.6-25-9.6-6-2-16.9-5.2-24-7.1-7.1-1.9-19.5-4.6-27.5-6-8-1.4-19.7-3.2-26-4-6.3-0.7-21-1.9-32.5-2.6-16.3-1-25.9-1-43-0.1-12.1 0.7-27.4 1.9-34 2.7-6.6 0.8-17.2 2.4-23.5 3.5-6.3 1.2-17.6 3.6-25 5.5-7.4 1.9-17.6 4.8-22.5 6.4-4.9 1.5-12.6 4.2-17 5.9-4.4 1.6-13.4 5.3-20 8.2-6.6 2.8-18.1 8.5-25.5 12.7-7.4 4.1-18.7 10.9-25 15.1-6.3 4.2-17.1 12.1-24 17.6-6.9 5.4-20.2 17.6-29.6 27-9.4 9.4-21.8 23-27.4 30.1-5.6 7.1-14.9 20-20.5 28.5-5.6 8.5-12.8 20.2-16 26-3.3 5.8-8.7 16.4-12.1 23.5-3.4 7.1-8.6 19.3-11.7 27-3 7.7-7.3 19.6-9.5 26.5-2.3 6.9-5.7 18.6-7.6 26-2 7.4-4.7 18.9-6 25.5-1.3 6.6-3.3 18.3-4.5 26-1.1 7.7-2.6 19.4-3.3 26-0.7 7.1-1.2 23.5-1.1 40.5 0 19 0.6 33.6 1.7 44 1 8.5 2.8 21.6 4.2 29 1.4 7.4 3.4 17.1 4.5 21.5 1.1 4.4 3.9 13.6 6.1 20.5 2.2 6.9 5.8 16.8 8 22 2.1 5.2 6.6 14.7 9.9 21 3.4 6.3 8.6 15.3 11.6 20 3.1 4.7 8.4 12.1 11.8 16.5 3.4 4.4 10.5 12.3 15.7 17.5 5.1 5.2 13.9 12.9 19.3 17 5.5 4.2 13.8 9.9 18.5 12.6 4.7 2.8 13.2 7.2 19 9.7 5.8 2.5 14.1 5.6 18.5 7 4.4 1.4 12.5 3.5 18 4.6 5.5 1.1 14.7 2.6 20.5 3.2 6.1 0.7 16.6 0.9 25 0.6 8-0.3 18.6-1.3 23.5-2.2 4.9-0.9 13.5-3.1 19-4.9 5.5-1.9 12.9-4.8 16.5-6.6 3.6-1.8 10.3-5.8 14.9-8.9 4.6-3.1 11.4-8.5 15-12.1 3.6-3.6 8.9-9.7 11.8-13.5 2.9-3.9 7-10.4 9.2-14.5 2.2-4.1 5.3-11.5 6.9-16.5 1.7-5 3.8-13 4.8-18 1.4-6.8 1.8-13.6 1.8-28 0-10.8-0.5-22.2-1.3-26.5-0.7-4.1-2.3-11.1-3.6-15.5-1.4-4.8-3.7-9.8-5.7-12.5-1.9-2.5-4.5-5.1-5.9-5.9-2-1.2-2.8-1.2-5.4 0-1.8 0.9-4.2 3.5-6 6.7-1.6 2.8-5.3 9.1-8 13.8-2.8 4.7-7 11-9.5 13.9-2.5 2.9-6.8 7.2-9.5 9.4-2.8 2.3-7.7 5.5-11 7.2-3.7 1.8-9.6 3.6-15.5 4.6-7.9 1.3-10.8 1.4-17 0.4-4.6-0.7-10.3-2.4-14.5-4.5-5-2.5-8.8-5.3-13.5-10.3-4-4.2-8.1-9.9-10.7-14.8-2.2-4.4-5.2-11.6-6.6-16-1.4-4.4-3.2-11.8-4.1-16.5-0.9-4.7-2.1-15.5-2.7-24-0.8-11.3-0.8-19.5 0.1-30.5 0.6-8.3 2-20.4 3.1-27 1.1-6.6 3.2-16.5 4.6-22 1.3-5.5 4.2-15.2 6.3-21.5 2.1-6.3 6.2-16.7 9-23 2.8-6.3 7.7-15.8 10.8-21 3-5.2 8.1-13.1 11.3-17.5 3.1-4.4 9.2-12.1 13.6-17 4.4-4.9 10.9-11.5 14.4-14.6 3.5-3 8.9-7.4 11.9-9.6 3-2.3 9.8-6.7 15-9.7 5.2-3.1 14.2-7.6 20-10.1 5.8-2.4 15.9-5.7 22.5-7.4 6.6-1.6 18.1-3.7 25.5-4.5 7.4-0.9 17.4-1.6 22.2-1.6 4.9 0 14.5 0.5 21.5 1 7.1 0.6 17.3 1.8 22.8 2.6 5.5 0.9 14.5 2.7 20 4.1 5.5 1.3 16.2 4.5 23.7 6.9 7.6 2.4 15 4.4 16.5 4.4 1.6 0.1 3.5-0.7 4.3-1.6 1.1-1.2 1.3-2.8 0.8-6.3-0.4-2.5-2-7.3-3.7-10.6-1.6-3.3-5.6-9.1-8.8-13-3.3-3.9-9-9.6-12.9-12.8-3.8-3.2-10-8-13.9-10.5-3.9-2.6-10.8-6.6-15.5-8.9-4.7-2.3-13-5.8-18.5-7.8-5.5-2-13.8-4.6-18.5-5.7-4.7-1.1-13.2-2.6-19-3.4-5.8-0.7-16.4-1.4-23.5-1.4-7.1 0-17.7 0.4-23.5 1-5.8 0.6-13.6 1.5-17.5 2.1-3.9 0.6-12.2 2.4-18.5 3.9-6.3 1.6-15.1 4.2-19.5 5.8-4.4 1.6-12.1 4.8-17 7-4.9 2.2-11.3 5.3-14 6.8-2.8 1.5-10.6 6.4-17.5 11-6.9 4.5-16.8 11.8-22 16.1-5.2 4.4-14.2 12.5-20 17.9-5.8 5.5-12.1 10.6-14 11.4-3 1.2-3.8 1.1-6-0.1-2.1-1.2-2.6-2.3-2.8-6.4-0.1-2.8 0.5-7.3 1.4-10 0.9-2.8 4.3-10.2 7.6-16.5 3.2-6.3 8.9-15.7 12.6-20.9 3.7-5.2 11.9-14.9 18.2-21.4 6.3-6.6 15.1-14.9 19.5-18.4 4.4-3.6 12.7-9.6 18.5-13.4 5.8-3.9 15.9-9.7 22.5-12.9 6.6-3.2 16.3-7.5 21.5-9.6 5.2-2 13.1-4.7 17.5-6.1 4.4-1.3 12.7-3.4 18.5-4.8 5.8-1.3 15.9-3.2 22.5-4.2 9.3-1.4 18.3-1.8 40.5-1.8 25.4 0 30 0.3 42.5 2.4 7.7 1.3 20.1 4.1 27.5 6.3 7.4 2.1 17.8 5.7 23 7.9 5.2 2.3 14.2 6.8 20 10 5.8 3.3 14.8 9 20 12.7 5.4 3.8 15.6 12.7 23.6 20.7 7.7 7.7 16.8 17.6 20.1 22 3.3 4.4 8.5 11.8 11.6 16.5 3 4.7 8.3 13.9 11.8 20.5 5 9.6 6 12.2 4.8 12.8-0.8 0.4-5.7 3.4-10.9 6.6-5.2 3.3-13 8.5-17.4 11.8-4.3 3.2-11.1 9-15 12.8-3.9 3.9-9.6 10.2-12.7 14-3 3.9-7.3 9.7-9.6 13-2.3 3.3-5.3 7.9-9.3 14.5l-7.3-3.4c-3.9-1.8-13.1-5.5-20.2-8.1-7.1-2.6-18.6-6.2-25.5-8.1-6.9-1.9-17.9-4.6-24.5-5.9-6.6-1.3-16-2.9-21-3.5-5-0.6-15.3-1.1-23-1.1-7.7 0-16.5 0.5-19.5 1.1-3.7 0.7-6.1 1.8-7.3 3.3-1 1.2-2 4.2-2.4 6.7-0.3 2.5 0 8.3 0.7 13 0.6 4.7 2.3 12.3 3.7 17 1.3 4.7 4.7 13.7 7.4 20 2.8 6.3 8.1 16.7 11.9 23 3.7 6.3 9.6 15.3 13 20 3.4 4.7 10.6 12.8 15.9 18 5.3 5.2 13.4 12.1 18.1 15.2 4.7 3.1 13.2 7.9 19 10.7 5.8 2.7 14.5 6.3 19.5 8 5 1.6 12.4 3.6 16.5 4.6 4.1 0.9 14.6 1.9 38.9 3l1.5 5.5c0.8 3 2.1 8.6 2.8 12.5 1 5.2 1.3 20.2 1.2 57.5 0 28.3-0.5 59.1-1.2 70-0.7 10.7-1.9 27.4-2.8 37-0.8 9.6-2.3 24.9-3.4 34-1.1 9.1-3.2 23.9-4.6 33-1.4 9.1-4.1 24.6-6 34.5-1.9 9.9-5.9 27.9-8.9 40-2.9 12.1-7.6 29.9-10.4 39.5-2.8 9.6-8.1 26.7-11.9 38-3.8 11.3-9.7 27.7-13 36.5-3.3 8.8-9.3 24.1-13.5 34-4.1 9.9-11.5 26.8-16.3 37.5-4.9 10.7-18 38-29.2 60.5-11.2 22.5-24.7 50-29.9 61-5.2 11-12.9 27.7-17 37-4.1 9.3-10.8 25.3-14.8 35.5-4 10.2-9.5 25.2-12.3 33.5-2.8 8.2-7.1 22.2-9.6 31-2.5 8.8-6.3 24.1-8.5 34-2.3 9.9-5.6 27.2-7.5 38.5-1.9 11.3-4.6 29.7-6 41-1.4 11.3-3.5 29.7-4.6 41-1.1 11.3-2 20.8-2 21.2 0 0.5-92 0.8-409 0.8zm508.6 1390c0.5 0 1.4 0.4 2 0.8 0.6 0.4 1.6 1.6 2.1 2.7 0.6 1.1 1.4 13.5 1.7 29 0.4 14.8 1.1 35.1 1.7 45 0.5 9.9 1.7 24.5 2.5 32.5 0.8 8 2.5 21.5 3.9 30 1.3 8.5 2.8 17 4 22h-69l0.8-8.2c0.4-4.6 1.7-14.1 2.8-21.3 1.1-7.2 3.3-19.1 5-26.5 1.7-7.4 4.7-18.9 6.6-25.5 1.9-6.6 5.5-17.6 8-24.5 2.5-6.9 7.9-20.6 12.1-30.5 5.6-12.9 8.7-19 11.3-21.7 2-2.1 4-3.8 4.5-3.8zm554.4-992.4c4.1 0.3 10.2 0.9 13.5 1.5 3.3 0.6 9.4 2.4 13.5 3.9 4.1 1.6 10.9 5.3 15 8.2 4.1 2.9 10.4 8.2 13.9 11.8 3.5 3.6 9.2 10.8 12.7 16 3.4 5.2 8 13.3 10.2 18 2.1 4.7 5 11.5 6.3 15.2 1.3 3.7 2.2 6.9 1.9 7.2-0.3 0.3-7.7-0.2-16.5-1.1-13.6-1.4-17.3-1.5-24.5-0.4-4.7 0.7-11 2.3-14 3.5-3 1.2-7.5 3.6-10 5.3-2.5 1.6-7.1 5.4-10.3 8.4-3.6 3.4-6.1 5-6.7 4.4-0.4-0.6-2.1-4.2-3.7-8-1.6-3.9-4.8-10.2-7-14-2.3-3.9-6.8-10.6-10-15-3.3-4.4-9.3-11.4-13.4-15.6-4.1-4.1-11-10.2-15.4-13.5-4.4-3.3-10.6-7.4-13.8-9.2-3.1-1.7-5.7-3.7-5.7-4.2 0-0.6 2.1-2.8 4.7-5.1 2.7-2.2 8.8-6 13.8-8.4 5-2.4 11.9-5.1 15.5-6 3.6-0.9 10.1-2.1 14.5-2.5 4.4-0.5 11.4-0.6 15.5-0.4zm-310.5 124.9c8.7 0.2 15.5 1 20.5 2.3 4.1 1.1 10.9 3.4 15 5.1 4.1 1.7 11.3 5.4 16 8.3 4.7 2.9 11.9 8.5 16 12.4 4.1 3.9 8.2 8.3 9 9.8 1.3 2.4 1.3 2.8 0 4.2-1.2 1.3-2.2 1.4-5 0.6-1.9-0.6-5.1-1.3-10.5-2.2l-0.6 7.2c-0.4 5.4-1.3 8.7-3.5 12.7-1.6 3-4.3 6.4-5.9 7.7-1.6 1.2-5.7 3.5-9 5-5 2.3-7.8 2.8-17 3.2-6.2 0.2-13.4 0-16.5-0.7-3-0.6-8.4-2.5-12-4.3-3.6-1.8-8.9-5.6-11.8-8.3-3-2.8-7.1-7.7-9.1-11-2.1-3.3-4.6-8.1-5.7-10.7-1.7-4.2-2.2-4.8-4.9-4.8-1.6 0.1-5.7 1.4-9 2.9-4.3 2.1-6.5 2.7-7.8 2-0.9-0.5-2-2.1-2.3-3.7-0.4-1.5-0.1-4.7 0.6-7.2 0.6-2.5 2.6-6.7 4.3-9.4 1.8-2.7 5.2-6.7 7.7-8.9 2.5-2.1 6.5-4.9 9-6.2 2.5-1.2 6.8-2.9 9.7-3.7 2.9-0.8 6.3-1.7 7.5-2.1 1.3-0.3 8.2-0.4 15.3-0.2zm227.5 196.6c1.9-0.1 8.9 0.6 15.5 1.4 6.6 0.8 19.4 1.8 28.5 2.1 12.6 0.5 19.2 0.3 28-1 6.3-0.9 13.9-1.6 16.7-1.6 2.9 0 6.4 0.6 7.8 1.2 1.4 0.7 3.3 2.9 4.2 4.8 1.2 2.7 1.5 6 1.2 15-0.4 9.2-0.8 12.1-2.4 14.8-1.2 1.8-3.6 3.8-5.8 4.7-2 0.8-5.2 1.5-7 1.5-1.7 0-5 0.5-7.2 1.1-2.2 0.6-5 2.1-6.3 3.2-1.2 1.2-3.4 5.1-4.7 8.7-1.3 3.6-3.1 6.8-3.9 7.2-0.9 0.5-2.7 0.8-4.1 0.8-1.6 0-3.3-1.1-4.8-2.9-1.2-1.6-3.5-4.9-5.2-7.2-1.7-2.4-4.6-5.3-6.5-6.5-1.9-1.3-6.2-3-9.5-3.9-4.5-1.2-10.1-1.5-23-1.3-14.6 0.3-17.5 0.1-20.3-1.5-2.1-1.1-4.1-3.5-5.7-7-2-4.1-2.6-6.9-2.7-13.2-0.1-5.7 0.3-8.9 1.4-11 0.9-1.7 2.7-4 4-5.2 1.2-1.3 3.7-2.7 5.3-3.2 1.6-0.5 4.6-1 6.5-1z"
      />
    </svg>
  );
}

export function HumanInterestLogo({ size = 18 }: LogoProps) {
  // Official Human Interest mark — teal dot atop a white "H" body.
  // The source uses pure white for the body, which only reads on dark
  // backgrounds; on the dark theme that's fine.
  return (
    <svg
      role="img"
      aria-label="Human Interest"
      viewBox="0 0 74.6 109.6"
      width={size}
      height={size}
    >
      <path
        fill="#14B5AB"
        d="M58.2,32.8c9,0,16.4-7.3,16.4-16.4S67.2,0,58.2,0S41.8,7.3,41.8,16.4S49.1,32.8,58.2,32.8z"
      />
      <path
        fill="#FFFFFF"
        d="M69.2,48.8c-2.9-4.5-6.9-6-12.8-6.1c-5-0.1-11.8,2.3-21.5,7.2c-6.9,3.5-11.3,7-11.3,7V9.7c0-2.9-2.3-5.2-5.2-5.2c-0.9,0-1.6,0.2-2.4,0.6L0,12.8v96.8h16.2c4,0,7.3-3.3,7.3-7.3V69.5c0,0,3.2-2.6,8.1-5c4-2,7.6-3.3,10.4-3.3c3.5,0.1,6.3,1.8,6.3,5.7v35.3c0,4,3.3,7.3,7.3,7.3h15.7V57.4C71.2,54.7,70.7,51.3,69.2,48.8z"
      />
    </svg>
  );
}

export function LPLFinancialLogo({ size = 18 }: LogoProps) {
  // Official LPL Financial mark in #081D4D — nested square / inverted-L motif.
  return (
    <svg
      role="img"
      aria-label="LPL Financial"
      viewBox="0 0 63.3 63.4"
      width={size}
      height={size}
    >
      <path
        fill="#081D4D"
        d="M0,36.2h27.1v27.2h13V23.2H0V36.2z M0,0v13h50.3v50.4h13V0H0z M0,63.4h16.9V46.4H0V63.4z"
      />
    </svg>
  );
}

export function ZillowLogo({ size = 18 }: LogoProps) {
  // Official Zillow house-shape mark in #006AFF.
  return (
    <svg role="img" aria-label="Zillow" viewBox="0 0 107.5 118" width={size} height={size}>
      <g fill="#006AFF">
        <path d="M72.7,31.8c0.5-0.1,0.8,0.1,1.1,0.4c1.8,2.1,7.8,9.3,9.4,11.3c0.2,0.3,0.2,0.7-0.1,0.9c0,0-0.1,0.1-0.1,0.1C71,54,60,64.8,50.2,76.6c-0.1,0.2,0,0.2,0.1,0.1c13-5.6,43.4-14.5,57.1-17V42.4L53.8,0L0.1,42.4v19C16.7,51.5,55.2,36.2,72.7,31.8L72.7,31.8z" />
        <path d="M29,104.8c-0.4,0.2-0.9,0.1-1.1-0.2l-10-11.9c-0.3-0.3-0.3-0.5,0.1-1c7.7-11.3,23.5-29,33.6-36.5c0.2-0.2,0.2-0.3-0.1-0.2C40.9,58.4,11.1,71.2,0,77.5V118h107.5V79C92.8,81.5,48.9,94.5,29,104.8z" />
      </g>
    </svg>
  );
}

export function YouCamPerfectLogo({ size = 18 }: LogoProps) {
  // Official YouCam Perfect camera-iris mark — concentric purple rings
  // built from two radial gradients plus a lavender ovoid + light highlight
  // disc and a soft pink crescent. Gradient IDs are generated per-instance
  // via useId so multiple YouCam logos on the same page don't collide on
  // the document-global SVG id namespace (same pattern as VerizonLogo).
  const uid = useId().replace(/:/g, '');
  const outerGradId = `youcam-outer-${uid}`;
  const innerGradId = `youcam-inner-${uid}`;
  return (
    <svg
      role="img"
      aria-label="YouCam Perfect"
      viewBox="0 0 290.8 290.8"
      width={size}
      height={size}
    >
      <defs>
        <radialGradient
          id={outerGradId}
          cx="145.4"
          cy="145.4"
          fx="145.4"
          fy="145.4"
          r="130.3"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.5" stopColor="#9558B7" />
          <stop offset="0.8" stopColor="#B85BDF" />
          <stop offset="1" stopColor="#964AC6" />
        </radialGradient>
        <radialGradient
          id={innerGradId}
          cx="147.6"
          cy="201.3"
          fx="147.6"
          fy="201.3"
          r="111.3"
          gradientTransform="translate(265.6 -21.4) rotate(80.8)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.6" stopColor="#4334B3" />
          <stop offset="1" stopColor="#2F2192" />
        </radialGradient>
      </defs>
      <path
        fill="#FFFFFF"
        d="M145.4,0C65.1,0,0,65.1,0,145.4s65.1,145.4,145.4,145.4,145.4-65.1,145.4-145.4S225.7,0,145.4,0ZM145.4,275.7c-72,0-130.3-58.4-130.3-130.3S73.4,15,145.4,15s130.3,58.4,130.3,130.3-58.4,130.3-130.3,130.3Z"
      />
      <circle
        cx="145.4"
        cy="145.4"
        r="130.3"
        fill="none"
        stroke="#FF9D00"
        strokeMiterlimit="10"
      />
      <path
        fill={`url(#${outerGradId})`}
        d="M145.4,15C73.4,15,15,73.4,15,145.4s58.4,130.3,130.3,130.3,130.3-58.4,130.3-130.3S217.4,15,145.4,15ZM145.4,227.8c-45.5,0-82.4-36.9-82.4-82.4s36.9-82.4,82.4-82.4,82.4,36.9,82.4,82.4-36.9,82.4-82.4,82.4Z"
      />
      <path
        fill="#FFFFFF"
        d="M145.4,62.9c-45.5,0-82.4,36.9-82.4,82.4s36.9,82.4,82.4,82.4,82.4-36.9,82.4-82.4-36.9-82.4-82.4-82.4ZM145.4,213.6c-37.7,0-68.2-30.5-68.2-68.2s30.5-68.2,68.2-68.2,68.2,30.5,68.2,68.2-30.5,68.2-68.2,68.2Z"
      />
      <circle
        cx="145.4"
        cy="145.4"
        r="68.2"
        fill={`url(#${innerGradId})`}
        transform="translate(-21.4 265.6) rotate(-80.8)"
      />
      <path
        fill="#A198D7"
        d="M159.8,99c0,37.9-30.8,68.7-68.7,68.7s-7-.3-10.4-.8c-2.2-6.8-3.5-14-3.5-21.5,0-37.7,30.5-68.2,68.2-68.2s7.5.3,11.2.9c2.1,6.6,3.2,13.6,3.2,20.9Z"
      />
      <circle cx="105.4" cy="139.3" r="14.2" fill="#D8D6EE" />
      <path
        fill="#E9B8E7"
        d="M149.7,63c-1.4,0-2.8-.1-4.3-.1-45.5,0-82.4,36.9-82.4,82.4s.6,11.6,1.8,17.1c-24-10-41.1-33.1-42.3-60.4,10.8-30.7,32.8-56.1,61-71.4,2.5-.3,5-.4,7.6-.4,24.8,0,46.5,13.1,58.6,32.8Z"
      />
    </svg>
  );
}

export function ElComercioLogo({ size = 18 }: LogoProps) {
  // El Comercio tile mark — black silhouette on the paper's signature
  // yellow (#E7C727). Designed as a full-bleed badge, so the yellow
  // rect is part of the artwork rather than a transparent backdrop.
  return (
    <svg
      role="img"
      aria-label="El Comercio"
      viewBox="0 0 400 400"
      width={size}
      height={size}
    >
      <rect width="400" height="400" fill="#E7C727" />
      <path
        fill="#000000"
        d="M337.8,265.2c-1.7-1.7-17,4.5-34.2,13.8-17.2,9.3-42.2,19-55.8,21.5l-24.6,4.6V101.9l27.8,2.9c29.6,3,67.1-14.2,74.8-34.3,6.1-15.9-1.8-18.1-20.7-5.7-16.8,11-18.5,10.9-46.5-2.7l-29.1-14.1-45.8,37.4-45.8,37.4v64.2c0,67.2-5.5,78.1-22.8,44.8-21.6-41.7-2.8-112.7,37.2-140.7,19.9-14,10.5-19.9-12.6-8-29.5,15.3-65.8,59.5-74.1,90.3-16.9,62.8,15.1,132.3,74,160.5,44.1,21.1,93.9,18.7,140.4-6.8,28.9-15.8,65-54.5,57.7-61.8ZM159.2,283.1l-21.5-13.8,26.3-28.6,26.4-28.6v-55.5c0-30.5,3-57.3,6.6-59.5.1,0,.2-.1.3-.1,4-.3,6.2,33.8,6.2,98,0,114.1-.8,116-44.3,88.1Z"
      />
    </svg>
  );
}

export function WeFunderLogo({ size = 18 }: LogoProps) {
  // Official Wefunder stylized "W" mark in #3077B8.
  return (
    <svg role="img" aria-label="Wefunder" viewBox="0 0 50 50" width={size} height={size}>
      <path
        fill="#3077B8"
        d="M15.8,44.1c-1.3,0-2.5-.7-3.2-1.9l-5.9-11.2-1.9,3.3-4.8-2.8,3.8-6.5c.7-1.1,1.8-1.8,3.2-1.8,1.3,0,2.5.8,3.1,1.9l5.7,10.9,6.7-14.2c.6-1.2,1.7-2,3-2,1.3,0,2.5.6,3.2,1.7l7,11.3,9.1-26,5.3,1.8-10.5,30c-.5,1.3-1.6,2.2-3,2.4-1.4.1-2.7-.5-3.4-1.7l-7.2-11.8-6.8,14.5c-.6,1.2-1.8,2-3.1,2h0Z"
      />
    </svg>
  );
}

export function UniversityOfWashingtonLogo({ size = 18 }: LogoProps) {
  // University of Washington block "W" in Husky gold (#B7A57A). The source
  // SVG carried five hidden lockup variants; only the visible W path is kept.
  return (
    <svg
      role="img"
      aria-label="University of Washington"
      viewBox="0 0 108 72.6"
      width={size}
      height={size}
    >
      <path
        fill="#B7A57A"
        d="M79.3,0v13.1h9.2l-10.3,38.3S65.6.7,65.4,0h-13.3c-.2.7-13.8,51.4-13.8,51.4L28.9,13.1h9.6V0H0v13.1h8.5s14.7,58.8,14.9,59.5h20.6c.2-.7,9.9-37.5,9.9-37.5,0,0,9.2,36.7,9.4,37.5h20.6c.2-.7,15.7-59.5,15.7-59.5h8.4V0h-28.7Z"
      />
    </svg>
  );
}

// --- Multiple-platform indicator ---

export function MultipleLogo({ size = 18 }: LogoProps) {
  return (
    <svg role="img" aria-label="Multiple platforms" viewBox="0 0 24 24" width={size} height={size}>
      <rect
        x="3"
        y="9"
        width="14"
        height="11"
        rx="2"
        fill="none"
        stroke="var(--berg-text-secondary)"
        strokeWidth="1.5"
      />
      <rect
        x="6"
        y="6"
        width="14"
        height="11"
        rx="2"
        fill="var(--berg-bg-elevated)"
        stroke="var(--berg-text-secondary)"
        strokeWidth="1.5"
      />
      <rect
        x="9"
        y="3"
        width="12"
        height="11"
        rx="2"
        fill="var(--berg-bg-elevated)"
        stroke="var(--berg-text)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

// --- Generic fallback for unknown locations ---

function GenericLogo({ letter, size = 18 }: { letter: string; size?: number }) {
  return (
    <svg role="img" aria-label={letter} viewBox="0 0 24 24" width={size} height={size}>
      <circle cx="12" cy="12" r="11" fill="var(--berg-bg-subtle)" stroke="var(--berg-border-strong)" />
      <text
        x="12"
        y="16"
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="var(--berg-text-secondary)"
        fontFamily="system-ui, sans-serif"
      >
        {letter}
      </text>
    </svg>
  );
}

export function AnytimeMailboxLogo({ size = 18 }: LogoProps) {
  // Official Anytime Mailbox mark — orange (#FF6600) open-envelope glyph.
  // Source viewBox is 26×20 (wide ~1.3:1); the tooltip carries the full name
  // on hover at icon-column sizes.
  return (
    <svg
      role="img"
      aria-label="Anytime Mailbox"
      viewBox="0 0 26 20"
      width={size}
      height={size}
    >
      <path
        fill="#FF6600"
        d="M25,0H1C.4,0,0,.4,0,1v17c0,1.1.9,2,2,2h22c1.1,0,2-.9,2-2V1c0-.6-.4-1-1-1ZM13,10.6L3.6,2h18.9l-9.4,8.6ZM9.3,10l-7.3,6.7V3.3l7.3,6.7ZM10.8,11.4l1.5,1.4c.4.4,1,.4,1.4,0l1.5-1.4,7.2,6.6H3.6l7.2-6.6ZM16.7,10l7.3-6.7v13.5l-7.3-6.7Z"
      />
    </svg>
  );
}

export function CloudflareLogo({ size = 18 }: LogoProps) {
  // Cloudflare orange (#F38020) — the cloud mark from simple-icons.
  return <SiCloudflare color="#F38020" size={size} />;
}

// Each entry is `[match-keys, render]`. Keys are matched as substrings against
// the lowercased input — so "Robinhood Login" (Links page) and "Robinhood"
// (Asset/Debt location column) both find this entry. Sorted longest-first so
// "Bank of America Login" wins over any shorter coincidental overlap, and
// "T. Rowe Price" wins over a hypothetical naked "T".
export function PugetSoundEnergyLogo({ size = 18 }: LogoProps) {
  // Puget Sound Energy — teal diamond with the white PSE letterforms.
  return (
    <svg role="img" aria-label="Puget Sound Energy" viewBox="0 0 46.4 46.4" width={size} height={size}>
      <path
        fill="#19636a"
        d="M46,22.3L24.1.4c-.6-.6-1.2-.6-1.8,0L.4,22.3c-.6.6-.6,1.2,0,1.8l21.9,21.9c.5.6,1.3.6,1.8,0l21.9-21.9c.6-.6.6-1.2,0-1.8Z"
      />
      <polygon
        fill="#fff"
        points="30.8 17.9 30 22 34.6 22 34.3 23.4 29.7 23.4 28.9 27.9 33.7 27.9 33.4 29.4 26.9 29.4 29.3 16.4 35.9 16.4 35.6 17.9 30.8 17.9"
      />
      <path
        fill="#fff"
        d="M22.4,19.6c0,2.4,4,2.4,4,5.5s-2.1,4.3-5.2,4.3-1.5,0-2.1-.3l.3-1.7c.5.2,1.3.4,2,.4,2,0,3.1-1,3.1-2.4,0-2.3-4-2.3-4-5.4s1.7-4,4.8-4,1.2,0,2,.2l-.3,1.6c-.6-.2-1.3-.3-1.8-.3-1.9,0-2.8,1.1-2.8,2.1Z"
      />
      <path
        fill="#fff"
        d="M15.5,16.3c-1.1,0-2.2,0-3.1,0l-2.5,13h1.7l1-5c.3,0,.8,0,1.1,0,3,0,5.8-2.1,5.8-5s-1.4-3.2-3.9-3.2ZM13.9,23c-.4,0-.8,0-1,0l1-5.1c.4,0,.8,0,1.4,0,1.8,0,2.4.8,2.4,2,0,1.9-1.7,3.3-3.7,3.3Z"
      />
    </svg>
  );
}

export function BelongLogo({ size = 18 }: LogoProps) {
  // Belong mark — white "b" disc with the brand's green smile beneath.
  // Portrait viewBox; preserveAspectRatio letterboxes it in the square slot.
  return (
    <svg role="img" aria-label="Belong" viewBox="0 0 104.4 158.7" width={size} height={size}>
      <path
        fill="#FFFFFF"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M90.7,0.1c7.6,0,13.7,6.1,13.7,13.7v38.4c0,0.4,0,0.7-0.1,1c-0.5,13.2-5.5,24.4-14.8,33.6c-9.9,9.7-22.3,14.6-37.3,14.6s-27.4-4.9-37.3-14.6S0,65.1,0,50.8s5-26.3,14.9-36.1S37.2,0,52.2,0c0.6,0,1.1,0,1.7,0h36.5l0,0C90.5,0.1,90.6,0.1,90.7,0.1z M72,70.5c5.1-5.1,7.7-11.6,7.7-19.6S77.1,36.3,72,31.2s-11.7-7.7-19.7-7.7s-14.6,2.6-19.7,7.7s-7.7,11.7-7.7,19.7s2.6,14.6,7.7,19.6c5.1,5.1,11.7,7.6,19.7,7.6S66.8,75.5,72,70.5z"
      />
      <path
        fill="#3EE4A9"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M85.6,112.9c-5.1,0-9.8,2.9-11.9,7.7c-1,2.4-2.4,4.5-4.1,6.4c-4.5,5.2-10.2,7.8-17.2,7.8s-12.8-2.6-17.2-7.8c-1.7-1.9-3-4.1-4.1-6.4c-2.1-4.8-6.9-7.7-11.9-7.7c-10.2,0-16.3,11.5-11.1,20.6c2.1,3.6,4.7,7.1,7.8,10.2c9.7,10,21.9,15,36.6,15s26.9-5,36.6-15c3.1-3.2,5.7-6.6,7.8-10.2C102,124.4,95.8,112.9,85.6,112.9"
      />
    </svg>
  );
}

export function ZiplyFiberLogo({ size = 18 }: LogoProps) {
  // Ziply Fiber mark — the brand's green "Z".
  return (
    <svg role="img" aria-label="Ziply Fiber" viewBox="0 0 266.5 290" width={size} height={size}>
      <path
        fill="#2c963e"
        d="M0,0v70h146.9L0,252.3v37.7h266.5v-70H120S266.5,37.7,266.5,37.7V0H0Z"
      />
    </svg>
  );
}

const LOGO_DISPATCH: Array<[string[], (size: number) => ReturnType<typeof RobinhoodLogo>]> = [
  [['missouri higher education loan authority'], (s) => <MOHELALogo size={s} />],
  [['empower federal credit union'], (s) => <EmpowerFCULogo size={s} />],
  [['kelley blue book'], (s) => <KelleyBlueBookLogo size={s} />],
  [['bank of america'], (s) => <BankOfAmericaLogo size={s} />],
  [['t. rowe price', 't rowe price', 't. rowe', 't rowe'], (s) => <TRowePriceLogo size={s} />],
  [['wheatland bank'], (s) => <WheatlandBankLogo size={s} />],
  [['goldman sachs'], (s) => <GoldmanSachsLogo size={s} />],
  [['human interest'], (s) => <HumanInterestLogo size={s} />],
  [['lpl financial'], (s) => <LPLFinancialLogo size={s} />],
  [['ford credit'], (s) => <FordLogo size={s} />],
  [['empower fcu'], (s) => <EmpowerFCULogo size={s} />],
  [['wells fargo'], (s) => <WellsFargoLogo size={s} />],
  [['optum bank'], (s) => <OptumLogo size={s} />],
  [['cash app', 'cashapp'], (s) => <CashAppLogo size={s} />],
  [['apple card'], (s) => <AppleLogo size={s} />],
  [['anthropic', 'claude code', 'claude.ai'], (s) => <AnthropicLogo size={s} />],
  [['chatgpt', 'chat gpt', 'openai'], (s) => <ChatGPTLogo size={s} />],
  [['dropbox'], (s) => <DropboxLogo size={s} />],
  [['microsoft', 'msft', 'microsoft 365', 'office 365'], (s) => <MicrosoftLogo size={s} />],
  [['at&t', 'at & t', ' att '], (s) => <ATTLogo size={s} />],
  [['national grid'], (s) => <NationalGridLogo size={s} />],
  [['william penn', 'legal & general', 'legal and general'], (s) => <WilliamPennLogo size={s} />],
  [['lemonade'], (s) => <LemonadeLogo size={s} />],
  [['travelers'], (s) => <TravelersLogo size={s} />],
  [['ocwa'], (s) => <OCWALogo size={s} />],
  [['ring'], (s) => <RingLogo size={s} />],
  [['jewelers mutual'], (s) => <JewelersMutualLogo size={s} />],
  [['amazon prime', 'amazon'], (s) => <AmazonPrimeLogo size={s} />],
  [['midwest loan services', 'midwest loan'], (s) => <MidwestLoanServicesLogo size={s} />],
  [['verizon'], (s) => <VerizonLogo size={s} />],
  [['spotify'], (s) => <SpotifyLogo size={s} />],
  [['youtube', 'you tube'], (s) => <YouTubeLogo size={s} />],
  [['squarespace'], (s) => <SquarespaceLogo size={s} />],
  [['anytime mailbox'], (s) => <AnytimeMailboxLogo size={s} />],
  [['cloudflare'], (s) => <CloudflareLogo size={s} />],
  [['jc penney', 'jcpenney'], (s) => <JCPenneyLogo size={s} />],
  [['robinhood'], (s) => <RobinhoodLogo size={s} />],
  [['discover'], (s) => <DiscoverLogo size={s} />],
  [['synchrony'], (s) => <SynchronyLogo size={s} />],
  [['apple'], (s) => <AppleLogo size={s} />],
  [['youcam perfect', 'youcam'], (s) => <YouCamPerfectLogo size={s} />],
  [['el comercio'], (s) => <ElComercioLogo size={s} />],
  [['university of washington', ' uw '], (s) => <UniversityOfWashingtonLogo size={s} />],
  [['wefunder'], (s) => <WeFunderLogo size={s} />],
  [['afterpay'], (s) => <AfterpayLogo size={s} />],
  [['coinbase'], (s) => <CoinbaseLogo size={s} />],
  [['bluevine'], (s) => <BluevineLogo size={s} />],
  [['citibank', 'citi bank', 'citi'], (s) => <CitiLogo size={s} />],
  [['bitmart'], (s) => <BitmartLogo size={s} />],
  [['td bank', ' td '], (s) => <TDBankLogo size={s} />],
  [['nelnet'], (s) => <NelnetLogo size={s} />],
  [['mohela'], (s) => <MOHELALogo size={s} />],
  [['paypal'], (s) => <PayPalLogo size={s} />],
  [['affirm'], (s) => <AffirmLogo size={s} />],
  [['webull'], (s) => <WebullLogo size={s} />],
  [['ledger'], (s) => <LedgerLogo size={s} />],
  [['zillow'], (s) => <ZillowLogo size={s} />],
  [['sezzle'], (s) => <SezzleLogo size={s} />],
  [['klarna'], (s) => <KlarnaLogo size={s} />],
  [['optum'], (s) => <OptumLogo size={s} />],
  [['goldman'], (s) => <GoldmanSachsLogo size={s} />],
  [["macy's", 'macys'], (s) => <MacysLogo size={s} />],
  [['chase'], (s) => <ChaseLogo size={s} />],
  [['ford'], (s) => <FordLogo size={s} />],
  [['lpl'], (s) => <LPLFinancialLogo size={s} />],
  [['kbb'], (s) => <KelleyBlueBookLogo size={s} />],
  [['sofi'], (s) => <SoFiLogo size={s} />],
  [['bofa'], (s) => <BankOfAmericaLogo size={s} />],
  [['puget sound', 'puget', ' pse '], (s) => <PugetSoundEnergyLogo size={s} />],
  [['ziply'], (s) => <ZiplyFiberLogo size={s} />],
  [['belong'], (s) => <BelongLogo size={s} />],
  [['zip'], (s) => <ZipLogo size={s} />],
];

function logoFor(name: string, size: number) {
  // Pad with spaces so word-boundary-ish matches work for short ambiguous keys
  // (e.g. " td " avoids matching the "td" inside "today" or "ltd"). Most keys
  // are distinctive enough not to need this, but the few short ones use it.
  const haystack = ` ${name.trim().toLowerCase()} `;
  for (const [keys, render] of LOGO_DISPATCH) {
    if (keys.some((k) => haystack.includes(k))) return render(size);
  }
  return <GenericLogo letter={(name.trim()[0] ?? '?').toUpperCase()} size={size} />;
}

// Returns true if `name` matches a registered LOGO_DISPATCH entry — i.e. it
// will render a real brand mark rather than the letter-badge fallback. Used by
// SettingsPage to surface "missing logo" notices for unmatched banks.
export function hasLogo(name: string): boolean {
  const haystack = ` ${name.trim().toLowerCase()} `;
  return LOGO_DISPATCH.some(([keys]) => keys.some((k) => haystack.includes(k)));
}

// Public dispatcher used by AssetsPage. Returns null for blank / N/A. Wraps
// the picked logo in a Tooltip showing the original location string —
// unless `noTooltip` is set (e.g. on the kitchen kiosk, which has no
// hover input and where the tooltip would just be visual noise).
export function ThirdPartyLogo({
  location,
  size = 18,
  noTooltip = false,
}: {
  location: string;
  size?: number;
  noTooltip?: boolean;
}) {
  const trimmed = location.trim();
  if (!trimmed || trimmed.toLowerCase() === 'n/a') return null;

  const isMultiple = trimmed.includes(',') || trimmed.toLowerCase() === 'multiple';
  const logo = isMultiple ? <MultipleLogo size={size} /> : logoFor(trimmed, size);
  if (noTooltip) return logo;
  return <Tooltip content={trimmed}>{logo}</Tooltip>;
}
